import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import {
  Save,
  ChevronDown,
  Eye,
  EyeOff,
  Link as LinkIcon,
  ExternalLink,
  MessageCircle,
  Edit,
  RefreshCw,
  Sparkles,
  Check,
  RotateCw,
  X,
  Plus,
  ChevronUp,
  ChevronDown as ChevronDownIcon,
} from "lucide-react";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { db } from "../../../../firebase";
import responseService from "../../services/responseService";
import questionnaireService from "../../services/questionnaireService";
import narrativeService from "../../../../services/narrativeService";
import NarrativeSection from "../../../../Components/NarrativeSection";
import { SECTION_TYPES } from "../../models/types";
import { useNavigate } from "react-router-dom";
import ScaleSlider from "../ScaleSlider";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { storage } from "../../../../firebase";

/**
 * DynamicProfileEditor component
 * Editor for questionnaire response data
 */
const DynamicProfileEditor = ({ responseId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedNarratives, setExpandedNarratives] = useState({});
  const [visibility, setVisibility] = useState({});
  const [customUrlName, setCustomUrlName] = useState("");
  const [customUrlPreview, setCustomUrlPreview] = useState("");
  const [urlExists, setUrlExists] = useState(false);
  const [deliveryDriveUrl, setDeliveryDriveUrl] = useState("");
  const [narratives, setNarratives] = useState({});
  const [narrativeEdits, setNarrativeEdits] = useState({});
  const [optimizedResponses, setOptimizedResponses] = useState({});
  const [optimizing, setOptimizing] = useState({});
  const [showOptimized, setShowOptimized] = useState({});
  const [useOptimizedInProfile, setUseOptimizedInProfile] = useState(false);
  const [optimizedEdits, setOptimizedEdits] = useState({});
  const [optimizedContent, setOptimizedContent] = useState({});
  const [optimizingContent, setOptimizingContent] = useState({});
  const [showOptimizedContent, setShowOptimizedContent] = useState({});
  const [optimizedContentEdits, setOptimizedContentEdits] = useState({});
  const [visibilityPages, setVisibilityPages] = useState({
    discovery: true,
    soundboard: true,
    delivery: true,
  });
  const [visibilitySections, setVisibilitySections] = useState({});
  const [defaultTab, setDefaultTab] = useState("discovery");
  const [theme, setTheme] = useState({
    primary: "#7C3AED",
    secondary: "#f25022",
    accent: "#ffb900",
  });

  // Brand Images state management
  const [brandImages, setBrandImages] = useState({
    logo: null,
    hero: [],
    other: [],
    soundboard: [],
    delivery: [],
  });
  const [uploadingImages, setUploadingImages] = useState({});
  const [imageUploadProgress, setImageUploadProgress] = useState({});
  const [activeHeroImageId, setActiveHeroImageId] = useState(null);
  const [questionTitles, setQuestionTitles] = useState({});
  const [sectionSummaries, setSectionSummaries] = useState({});

  // Fetch the response data and questionnaire structure
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!responseId) {
          setError("No response ID provided");
          setLoading(false);
          return;
        }

        // Fetch response data
        const response = await responseService.getResponseById(responseId);

        if (!response) {
          setError("Response not found");
          setLoading(false);
          return;
        }

        // Fetch the questionnaire template
        const questionnaireData =
          await questionnaireService.getQuestionnaireById(
            response.questionnaireId
          );

        if (!questionnaireData) {
          setError("Questionnaire template not found");
          setLoading(false);
          return;
        }

        // Initialize expanded sections state
        const initialExpandedState = {};
        questionnaireData.sections.forEach((section) => {
          initialExpandedState[section.id] = false;
        });
        // Add custom sections that should be expanded by default
        initialExpandedState.brandImages = false;
        initialExpandedState.soundboard = false;
        initialExpandedState.delivery = false;
        setExpandedSections(initialExpandedState);

        // Initialize expanded narratives state
        const initialNarrativeState = {};
        const sectionTypes = [
          "brandCharacteristics",
          "brandBackground",
          "brandPersonality",
          "brandStrategy",
          "demographics",
          "brandSound",
          "projectGoals",
        ];
        sectionTypes.forEach((section) => {
          initialNarrativeState[section] = false;
        });
        setExpandedNarratives(initialNarrativeState);

        // Initialize visibility state for all questions
        const initialVisibility = {};

        // Check if response has existing visibility settings
        const existingVisibility = response.metadata?.visibility || {};

        questionnaireData.sections.forEach((section) => {
          (section.questions || []).forEach((question) => {
            // Use existing visibility settings if available, otherwise default to true
            initialVisibility[question.id] = existingVisibility[
              question.id
            ] || {
              visibleInEditor: true,
              visibleInProfile: true,
            };
          });
        });

        setVisibility(initialVisibility);

        // Load any previously saved optimized responses
        if (response.metadata?.optimizedResponses) {
          setOptimizedResponses(response.metadata.optimizedResponses);

          // Initialize show optimized state based on existing optimized responses
          const initialShowOptimized = {};
          Object.keys(response.metadata.optimizedResponses).forEach(
            (questionId) => {
              initialShowOptimized[questionId] = true; // Default to showing optimized content
            }
          );
          setShowOptimized(initialShowOptimized);

          // Check if optimized content is enabled for the profile
          if (response.metadata?.useOptimizedInProfile !== undefined) {
            setUseOptimizedInProfile(response.metadata.useOptimizedInProfile);
          }
        }

        // Get custom URL name if available
        if (response.metadata?.deliveryDriveUrl) {
          setDeliveryDriveUrl(response.metadata.deliveryDriveUrl);
        }
        if (response.metadata?.customUrlName) {
          setCustomUrlName(response.metadata.customUrlName);
          setCustomUrlPreview(
            convertToUrlFormat(response.metadata.customUrlName)
          );
        } else {
          // Try to populate with brand name if available
          try {
            const brandInfoSection = Object.values(response.data || {}).find(
              (section) => section.sectionTitle === "Brand Information"
            );
            if (brandInfoSection && brandInfoSection.responses) {
              const brandNameQ = Object.values(brandInfoSection.responses).find(
                (q) => q.questionText === "Brand Name"
              );
              if (brandNameQ && brandNameQ.value) {
                setCustomUrlName(brandNameQ.value);
                setCustomUrlPreview(convertToUrlFormat(brandNameQ.value));
              }
            }
          } catch (error) {
            console.error("Error getting brand name:", error);
          }
        }

        // Set the response data
        setData(response);
        setQuestionnaire(questionnaireData);

        // Initialize brand images from existing data
        if (
          response.brandStyle &&
          response.brandStyle.images &&
          response.brandStyle.images.length > 0
        ) {
          const images = response.brandStyle.images;

          // Create a more robust unique ID generator that always generates new IDs
          const generateUniqueId = (type, index) => {
            const timestamp = Date.now();
            const randomSuffix = Math.random().toString(36).substring(2, 8);
            const microtime = performance.now().toString().replace(".", "");
            return `${type}_${timestamp}_${index}_${microtime}_${randomSuffix}`;
          };

          // Check for duplicate IDs in existing images
          const existingIds = images.map((img) => img.id).filter(Boolean);
          const hasDuplicates =
            existingIds.length !== new Set(existingIds).size;

          if (hasDuplicates) {
            console.warn(
              "[DynamicProfileEditor] Detected duplicate IDs in existing images, generating new unique IDs"
            );
          }

          const sortedImages = {
            logo: images.find((img) => img.type === "logo")?.url || null,
            hero: images
              .filter((img) => img.type === "hero")
              .map((img, index) => ({
                // Always generate a new unique ID to avoid duplicates
                id: generateUniqueId("hero", index),
                url: img.url,
                name: img.name || "Hero Image",
              })),
            other: images
              .filter((img) => img.type === "other")
              .map((img, index) => ({
                // Always generate a new unique ID to avoid duplicates
                id: generateUniqueId("other", index),
                url: img.url,
                name: img.name || "Background Image",
              })),
            soundboard: images
              .filter((img) => img.type === "soundboard")
              .map((img, index) => ({
                // Always generate a new unique ID to avoid duplicates
                id: generateUniqueId("soundboard", index),
                url: img.url,
                name: img.name || "Soundboard Image",
              })),
            delivery: images
              .filter((img) => img.type === "delivery")
              .map((img, index) => ({
                // Always generate a new unique ID to avoid duplicates
                id: generateUniqueId("delivery", index),
                url: img.url,
                name: img.name || "Delivery Image",
              })),
          };
          setBrandImages(sortedImages);

          // Set active hero image from metadata or default to first hero image
          const savedActiveHeroId = response.metadata?.activeHeroImageId;
          if (
            savedActiveHeroId &&
            sortedImages.hero.some((img) => img.id === savedActiveHeroId)
          ) {
            setActiveHeroImageId(savedActiveHeroId);
          } else if (sortedImages.hero.length > 0) {
            // Default to first hero image if no saved active image or saved ID doesn't exist
            setActiveHeroImageId(sortedImages.hero[0].id);
          }

          console.log(
            "[DynamicProfileEditor] Loaded existing images:",
            sortedImages
          );
        } else {
          console.log("[DynamicProfileEditor] No existing brand images found");
        }

        // Fetch narratives from the same response document
        try {
          const narrativeData = await narrativeService.getAllNarratives(
            responseId
          );
          setNarratives(narrativeData || {});
        } catch (error) {
          console.error("Error fetching narratives:", error);
        }

        setVisibilityPages(
          response.visibility?.pages || {
            discovery: true,
            soundboard: true,
            delivery: true,
          }
        );

        // Initialize visibility sections - all sections should be visible by default
        const existingSectionVisibility = response.visibility?.sections || {};
        const initialSectionVisibility = {};

        // Map questionnaire sections to visualizer section keys
        const sectionKeyMapping = {};
        questionnaireData.sections.forEach((section) => {
          let visualizerKey = "";
          if (section.title.toLowerCase().includes("background"))
            visualizerKey = "brandBackground";
          else if (section.title.toLowerCase().includes("characteristic"))
            visualizerKey = "brandCharacteristics";
          else if (section.title.toLowerCase().includes("personality"))
            visualizerKey = "brandPersonality";
          else if (section.title.toLowerCase().includes("strategy"))
            visualizerKey = "brandStrategy";
          else if (section.title.toLowerCase().includes("demographic"))
            visualizerKey = "demographics";
          else if (section.title.toLowerCase().includes("sound"))
            visualizerKey = "brandSound";
          else if (section.title.toLowerCase().includes("goals"))
            visualizerKey = "projectGoals";
          else if (
            section.title.toLowerCase().includes("information") ||
            section.title.toLowerCase().includes("info")
          )
            visualizerKey = "brandInfo";
          else
            visualizerKey = section.id; // Fallback to section ID

          // Use visualizer key for visibility, check both old (section.id) and new (visualizerKey) keys
          const visibilityValue = existingSectionVisibility[visualizerKey] !== undefined
            ? existingSectionVisibility[visualizerKey]
            : existingSectionVisibility[section.id] !== undefined
            ? existingSectionVisibility[section.id]
            : true; // Default to visible

          initialSectionVisibility[visualizerKey] = visibilityValue !== false;
        });

        setVisibilitySections(initialSectionVisibility);
        // Default tab and theme
        setDefaultTab(
          (response.visibility && response.visibility.defaultTab) || "discovery"
        );
        if (response.metadata && response.metadata.theme) {
          setTheme((prev) => ({ ...prev, ...response.metadata.theme }));
        }

        // Initialize question titles from metadata
        if (response.metadata && response.metadata.questionTitles) {
          setQuestionTitles(response.metadata.questionTitles);
        }

        // Initialize section summaries from metadata
        if (response.metadata && response.metadata.sectionSummaries) {
          setSectionSummaries(response.metadata.sectionSummaries);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load response data");
        setLoading(false);
      }
    };

    fetchData();
  }, [responseId]);

  // Check if a URL mapping already exists
  useEffect(() => {
    const checkUrlExists = async () => {
      if (!customUrlPreview) return;

      try {
        // Check if this URL name is already registered
        const urlMappingRef = doc(db, "urlMappings", customUrlPreview);
        const urlMappingDoc = await getDoc(urlMappingRef);

        // If the URL exists but belongs to this response, it's not a conflict
        if (urlMappingDoc.exists()) {
          const existingResponseId = urlMappingDoc.data().responseId;
          setUrlExists(existingResponseId !== responseId);
        } else {
          setUrlExists(false);
        }
      } catch (error) {
        console.error("Error checking URL mapping:", error);
      }
    };

    checkUrlExists();
  }, [customUrlPreview, responseId]);

  // Convert a string to URL-friendly format
  const convertToUrlFormat = (text) => {
    if (!text) return "";
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  // Handle custom URL name change
  const handleCustomUrlChange = (e) => {
    const value = e.target.value;
    setCustomUrlName(value);
    setCustomUrlPreview(convertToUrlFormat(value));
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Toggle narrative section expansion
  const toggleNarrativeSection = (sectionId) => {
    setExpandedNarratives((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Toggle visibility settings with auto-save
  const toggleVisibility = async (questionId, setting) => {
    const newVisibility = {
      ...visibility,
      [questionId]: {
        ...visibility[questionId],
        [setting]: !(visibility[questionId]?.[setting] !== false), // Default to true
      },
    };
    
    setVisibility(newVisibility);
    
    // Auto-save visibility changes
    try {
      if (!responseId) {
        console.error("No response ID for auto-save");
        return;
      }
      
      // Prepare the data to save - visibility is stored in metadata.visibility
      const dataToSave = {
        ...data,
        metadata: {
          ...(data.metadata || {}),
          visibility: newVisibility,
        },
      };
      
      // Also update local data state
      setData((prev) => ({
        ...prev,
        metadata: {
          ...(prev.metadata || {}),
          visibility: newVisibility,
        },
      }));
      
      await responseService.updateResponse(responseId, dataToSave);
      toast.success("Visibility updated", { duration: 2000 });
    } catch (error) {
      console.error("Error auto-saving visibility:", error);
      toast.error("Failed to save visibility change");
      // Revert the change on error
      setVisibility(visibility);
    }
  };

  // Update a response value
  const updateResponseValue = (questionId, value) => {
    setData((prev) => ({
      ...prev,
      responses: {
        ...prev.responses,
        [questionId]: {
          ...(prev.responses[questionId] || {}),
          questionId,
          value,
          timestamp: new Date().toISOString(),
          manuallyEdited: true,
        },
      },
    }));
  };

  // Update notes for a question
  const updateNotes = (questionId, notes) => {
    setData((prev) => ({
      ...prev,
      responses: {
        ...prev.responses,
        [questionId]: {
          ...(prev.responses[questionId] || {}),
          questionId,
          notes,
          timestamp: new Date().toISOString(),
        },
      },
    }));
  };

  // Update question title
  const updateQuestionTitle = (questionId, title) => {
    setQuestionTitles((prev) => ({
      ...prev,
      [questionId]: title,
    }));
  };

  // Toggle question title visibility (empty string means hidden)
  const toggleQuestionTitleVisibility = (questionId) => {
    const currentTitle = questionTitles[questionId];
    if (currentTitle === "") {
      // If hidden, restore original title
      const question = questionnaire?.sections
        .flatMap((s) => s.questions)
        .find((q) => q.id === questionId);
      if (question) {
        setQuestionTitles((prev) => ({
          ...prev,
          [questionId]: question.title,
        }));
      } else {
        setQuestionTitles((prev) => {
          const newTitles = { ...prev };
          delete newTitles[questionId];
          return newTitles;
        });
      }
    } else {
      // Hide title
      setQuestionTitles((prev) => ({
        ...prev,
        [questionId]: "",
      }));
    }
  };

  // Toggle notes visibility
  const toggleNotesVisibility = (questionId) => {
    setData((prev) => {
      const currentResponse = prev.responses[questionId] || {};
      return {
        ...prev,
        responses: {
          ...prev.responses,
          [questionId]: {
            ...currentResponse,
            questionId,
            notesVisible: !(currentResponse.notesVisible !== false), // Default to true
            timestamp: new Date().toISOString(),
          },
        },
      };
    });
  };

  // Handle narrative edits
  const handleNarrativeEdit = (section, value) => {
    setNarrativeEdits((prev) => ({
      ...prev,
      [section]: value || "", // Ensure value is never null
    }));
  };

  // Open the full profile view with the custom URL
  const openFullView = () => {
    const urlName = customUrlPreview || "brand-profile";
    navigate(`/brand-page/${urlName}`);
  };

  // Save changes to the database
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      if (!responseId) {
        setError("No response ID provided");
        setSaving(false);
        return;
      }

      // Prepare brand images for saving
      const brandStyleImages = [];

      // Add logo if exists
      if (brandImages.logo) {
        brandStyleImages.push({
          id: `logo_${Date.now()}`,
          type: "logo",
          url: brandImages.logo,
          name: "Logo",
        });
      }

      // Add hero images
      brandImages.hero.forEach((img) => {
        brandStyleImages.push({
          id: img.id,
          type: "hero",
          url: img.url,
          name: img.name,
        });
      });

      // Add other images
      brandImages.other.forEach((img) => {
        brandStyleImages.push({
          id: img.id,
          type: "other",
          url: img.url,
          name: img.name,
        });
      });

      // Add soundboard images
      brandImages.soundboard.forEach((img) => {
        brandStyleImages.push({
          id: img.id,
          type: "soundboard",
          url: img.url,
          name: img.name,
        });
      });

      // Add delivery images
      brandImages.delivery.forEach((img) => {
        brandStyleImages.push({
          id: img.id,
          type: "delivery",
          url: img.url,
          name: img.name,
        });
      });

      const dataToSave = {
        ...data,
        soundboard: data.soundboard || {},
        deliverySections: data.deliverySections || [],
        brandStyle: {
          ...(data.brandStyle || {}),
          images: brandStyleImages,
        },
        visibility: {
          pages: visibilityPages,
          sections: visibilitySections,
          defaultTab,
        },
        metadata: {
          ...(data.metadata || {}),
          optimizedContent,
          customUrlName, // ensure this is saved
          activeHeroImageId, // save the active hero image ID
          theme, // include theme in the consolidated metadata
          deliveryDriveUrl, // save the Google Drive URL
          questionTitles, // save custom question titles
          sectionSummaries, // save section summaries
        },
      };

      console.log(
        "[DynamicProfileEditor] Saving delivery sections in order:",
        dataToSave.deliverySections.map((section, index) => ({
          index,
          title: section.title || `Section ${index + 1}`,
        }))
      );

      await responseService.updateResponse(responseId, dataToSave);

      // --- Add this block ---
      if (customUrlPreview && !urlExists) {
        // Create or update the urlMappings document
        await setDoc(doc(db, "urlMappings", customUrlPreview), {
          responseId,
        });
      }
      // --- End block ---

      toast.success("Profile data saved!");
      setSaving(false);
    } catch (err) {
      console.error("Error saving profile data:", err);
      setError("Failed to save profile data");
      setSaving(false);
    }
  };

  // Auto-save function for delivery section reordering
  const autoSave = async (newDeliverySections) => {
    try {
      if (!responseId) {
        console.error("No response ID for auto-save");
        return;
      }

      // Prepare brand images for saving
      const brandStyleImages = [];

      // Add logo if exists
      if (brandImages.logo) {
        brandStyleImages.push({
          id: `logo_${Date.now()}`,
          type: "logo",
          url: brandImages.logo,
          name: "Logo",
        });
      }

      // Add hero images
      brandImages.hero.forEach((img) => {
        brandStyleImages.push({
          id: img.id,
          type: "hero",
          url: img.url,
          name: img.name,
        });
      });

      // Add other images
      brandImages.other.forEach((img) => {
        brandStyleImages.push({
          id: img.id,
          type: "other",
          url: img.url,
          name: img.name,
        });
      });

      // Add soundboard images
      brandImages.soundboard.forEach((img) => {
        brandStyleImages.push({
          id: img.id,
          type: "soundboard",
          url: img.url,
          name: img.name,
        });
      });

      // Add delivery images
      brandImages.delivery.forEach((img) => {
        brandStyleImages.push({
          id: img.id,
          type: "delivery",
          url: img.url,
          name: img.name,
        });
      });

      const dataToSave = {
        ...data,
        soundboard: data.soundboard || {},
        deliverySections: newDeliverySections,
        brandStyle: {
          ...(data.brandStyle || {}),
          images: brandStyleImages,
        },
        metadata: {
          ...(data.metadata || {}),
          optimizedContent,
          customUrlName,
          activeHeroImageId,
          deliveryDriveUrl,
          sectionSummaries,
        },
      };

      console.log(
        "[DynamicProfileEditor] Auto-saving delivery sections in order:",
        dataToSave.deliverySections.map((section, index) => ({
          index,
          title: section.title || `Section ${index + 1}`,
        }))
      );

      await responseService.updateResponse(responseId, dataToSave);
      console.log("[DynamicProfileEditor] Auto-save completed successfully");
    } catch (err) {
      console.error("[DynamicProfileEditor] Auto-save failed:", err);
      toast.error("Failed to auto-save changes");
    }
  };

  // Save narrative edits
  const saveNarrativeEdit = async (section) => {
    if (!narrativeEdits[section]) return;

    try {
      setSaving(true);

      // Skip if responseId is missing
      if (!responseId) {
        toast.error("Cannot save narrative - missing response ID");
        return;
      }

      // Get the response ref
      const responseRef = doc(db, "questionnaireResponses", responseId);
      const responseSnap = await getDoc(responseRef);

      if (responseSnap.exists()) {
        const responseData = responseSnap.data();
        const narratives = responseData.narratives || {};

        // Update the narrative
        narratives[section] = {
          ...narratives[section],
          text: narrativeEdits[section],
          manuallyEdited: true,
          lastEditedAt: new Date(),
        };

        // Update the response document
        await updateDoc(responseRef, { narratives });

        // Update local state
        setNarratives((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            text: narrativeEdits[section],
            manuallyEdited: true,
          },
        }));

        toast.success("Narrative saved successfully!");
      } else {
        toast.error("Could not find questionnaire response");
      }
    } catch (error) {
      console.error("Error saving narrative:", error);
      toast.error("Failed to save narrative");
    } finally {
      setSaving(false);
    }
  };

  // Regenerate narrative for a section
  const regenerateNarrative = async (section, sectionData) => {
    try {
      setSaving(true);

      if (!responseId) {
        toast.error("Cannot regenerate narrative - missing response ID");
        return;
      }

      toast.loading("Generating narrative...");

      // Force regeneration by calling the narrative service
      const newNarrative = await narrativeService.generateNarrative(
        responseId,
        section,
        sectionData,
        null, // companyUrl
        true // forceRegenerate
      );

      // Update local state with the new narrative
      setNarratives((prev) => ({
        ...prev,
        [section]: {
          text: newNarrative,
          generatedAt: new Date(),
          manuallyEdited: false,
        },
      }));

      // Clear any existing edits
      setNarrativeEdits((prev) => ({
        ...prev,
        [section]: undefined,
      }));

      toast.dismiss();
      toast.success("Narrative regenerated!");
    } catch (error) {
      console.error("Error regenerating narrative:", error);
      toast.dismiss();
      toast.error("Failed to regenerate narrative");
    } finally {
      setSaving(false);
    }
  };

  // Image Management Functions
  const handleImageUpload = async (file, imageType, imageId = null) => {
    try {
      // Generate a truly unique ID using multiple sources of randomness
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      const microtime = performance.now().toString().replace(".", "");
      const uploadId =
        imageId || `${imageType}_${timestamp}_${microtime}_${randomSuffix}`;

      console.log("[Image Upload] Generated upload ID:", uploadId);

      setUploadingImages((prev) => ({ ...prev, [uploadId]: true }));
      setImageUploadProgress((prev) => ({ ...prev, [uploadId]: 0 }));

      // Create storage reference
      const fileName = `${file.name
        .split(".")
        .slice(0, -1)
        .join(".")}_${timestamp}.${file.name.split(".").pop()}`;
      const storageRef = ref(
        storage,
        `brand-images/${responseId}/${imageType}/${fileName}`
      );

      // Upload file with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setImageUploadProgress((prev) => ({
              ...prev,
              [uploadId]: progress,
            }));
          },
          (error) => {
            console.error("Upload error:", error);
            setUploadingImages((prev) => ({ ...prev, [uploadId]: false }));
            toast.error("Failed to upload image");
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

              // Update brand images state
              setBrandImages((prev) => {
                const newState = { ...prev };

                if (imageType === "logo") {
                  newState.logo = downloadURL;
                  console.log("[Image Upload] Updated logo:", downloadURL);
                } else {
                  const imageData = {
                    id: uploadId,
                    url: downloadURL,
                    name: file.name,
                    uploadedAt: new Date().toISOString(),
                  };

                  console.log("[Image Upload] Creating image data:", imageData);

                  if (imageId) {
                    // Replace existing image
                    const index = newState[imageType].findIndex(
                      (img) => img.id === imageId
                    );
                    if (index !== -1) {
                      newState[imageType][index] = imageData;
                      console.log(
                        "[Image Upload] Replaced existing image at index:",
                        index
                      );
                    }
                  } else {
                    // Add new image
                    newState[imageType] = [...newState[imageType], imageData];
                    console.log(
                      "[Image Upload] Added new image. New array length:",
                      newState[imageType].length
                    );
                  }
                }

                console.log("[Image Upload] Final state:", newState);
                return newState;
              });

              setUploadingImages((prev) => ({ ...prev, [uploadId]: false }));
              setImageUploadProgress((prev) => ({ ...prev, [uploadId]: 100 }));

              toast.success(
                `${
                  imageType.charAt(0).toUpperCase() + imageType.slice(1)
                } image uploaded successfully!`
              );
              resolve(uploadId);
            } catch (error) {
              console.error("Error getting download URL:", error);
              setUploadingImages((prev) => ({ ...prev, [uploadId]: false }));
              toast.error("Failed to get image URL");
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      const microtime = performance.now().toString().replace(".", "");
      const uploadId =
        imageId ||
        `${imageType}_${timestamp}_${microtime}_${randomSuffix}_error`;
      setUploadingImages((prev) => ({ ...prev, [uploadId]: false }));
      toast.error("Failed to upload image");
      throw error;
    }
  };

  const handleImageDelete = (imageType, imageId = null) => {
    console.log("[Image Delete] Attempting to delete:", { imageType, imageId });

    // Check if we're deleting the active hero image
    const isDeletingActiveHero =
      imageType === "hero" && imageId === activeHeroImageId;

    setBrandImages((prev) => {
      const newState = { ...prev };

      console.log("[Image Delete] Current state before deletion:", prev);
      console.log("[Image Delete] Current array for type:", prev[imageType]);

      if (imageType === "logo") {
        newState.logo = null;
        console.log("[Image Delete] Deleted logo");
      } else if (imageId !== null && imageId !== undefined) {
        const originalArray = newState[imageType] || [];
        console.log(
          "[Image Delete] Original array length:",
          originalArray.length
        );
        console.log(
          "[Image Delete] Looking for ID:",
          imageId,
          "Type:",
          typeof imageId
        );
        console.log(
          "[Image Delete] All IDs in array:",
          originalArray.map((img) => ({ id: img.id, type: typeof img.id }))
        );

        // Check if the imageId exists in the array before filtering
        const imageExists = originalArray.some((img) => img.id === imageId);
        if (!imageExists) {
          console.warn(
            "[Image Delete] Image with ID",
            imageId,
            "not found in array"
          );
          return prev; // Return previous state unchanged if image not found
        }

        newState[imageType] = originalArray.filter((img) => {
          const shouldKeep = img.id !== imageId;
          console.log("[Image Delete] Image ID:", img.id, "Keep?", shouldKeep);
          return shouldKeep;
        });

        console.log(
          "[Image Delete] New array length:",
          newState[imageType].length
        );

        // Additional safety check - if we filtered out everything, something went wrong
        if (newState[imageType].length === 0 && originalArray.length > 1) {
          console.error(
            "[Image Delete] WARNING: All images were filtered out! This might be a bug."
          );
          console.error(
            "[Image Delete] Original array had",
            originalArray.length,
            "images"
          );
          console.error("[Image Delete] Looking for ID:", imageId);
          console.error(
            "[Image Delete] Available IDs:",
            originalArray.map((img) => img.id)
          );
          // Return previous state to prevent data loss
          return prev;
        }

        // If we deleted the active hero image, set a new active image
        if (
          isDeletingActiveHero &&
          imageType === "hero" &&
          newState.hero.length > 0
        ) {
          setActiveHeroImageId(newState.hero[0].id);
          console.log(
            "[Image Delete] Set new active hero image:",
            newState.hero[0].id
          );
        } else if (isDeletingActiveHero && newState.hero.length === 0) {
          setActiveHeroImageId(null);
          console.log(
            "[Image Delete] No hero images left, cleared active hero"
          );
        }
      } else {
        console.log("[Image Delete] No valid imageId provided:", imageId);
        return prev; // Return previous state unchanged
      }

      console.log("[Image Delete] Final state:", newState);
      return newState;
    });

    toast.success("Image removed successfully!");
  };

  const handleImageReorder = (imageType, dragIndex, hoverIndex) => {
    setBrandImages((prev) => {
      const newState = { ...prev };
      const draggedImage = newState[imageType][dragIndex];
      newState[imageType].splice(dragIndex, 1);
      newState[imageType].splice(hoverIndex, 0, draggedImage);
      return newState;
    });
  };

  // Handle moving delivery section up
  const moveDeliverySectionUp = async (index) => {
    if (index === 0) return; // Can't move first item up

    const newSections = [...(data.deliverySections || [])];
    const temp = newSections[index];
    newSections[index] = newSections[index - 1];
    newSections[index - 1] = temp;

    console.log(
      "[DynamicProfileEditor] Moving section up - new order:",
      newSections.map((section, idx) => ({
        index: idx,
        title: section.title || `Section ${idx + 1}`,
      }))
    );

    setData((prev) => ({
      ...prev,
      deliverySections: newSections,
    }));

    // Auto-save the changes
    await autoSave(newSections);
    toast.success("Section moved up and saved");
  };

  // Handle moving delivery section down
  const moveDeliverySectionDown = async (index) => {
    const sections = data.deliverySections || [];
    if (index === sections.length - 1) return; // Can't move last item down

    const newSections = [...sections];
    const temp = newSections[index];
    newSections[index] = newSections[index + 1];
    newSections[index + 1] = temp;

    console.log(
      "[DynamicProfileEditor] Moving section down - new order:",
      newSections.map((section, idx) => ({
        index: idx,
        title: section.title || `Section ${idx + 1}`,
      }))
    );

    setData((prev) => ({
      ...prev,
      deliverySections: newSections,
    }));

    // Auto-save the changes
    await autoSave(newSections);
    toast.success("Section moved down and saved");
  };

  const triggerImageUpload = (imageType, imageId = null) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = imageType !== "logo";

    input.onchange = async (e) => {
      const files = Array.from(e.target.files);

      if (files.length === 0) return;

      if (imageType === "logo") {
        await handleImageUpload(files[0], imageType, imageId);
      } else {
        // Handle multiple file uploads for non-logo types
        for (const file of files) {
          const uploadedId = await handleImageUpload(file, imageType);
          // If this is the first hero image uploaded, set it as active
          if (imageType === "hero" && !activeHeroImageId) {
            setActiveHeroImageId(uploadedId);
          }
        }
      }
    };

    input.click();
  };

  // Helper function to set active hero image
  const setActiveHeroImage = (imageId) => {
    setActiveHeroImageId(imageId);
    toast.success("Active hero image updated!");
  };

  // Helper function to get the active hero image
  const getActiveHeroImage = () => {
    return brandImages.hero.find((img) => img.id === activeHeroImageId);
  };

  // Function to handle optimizing a question response
  const handleOptimizeResponse = async (
    questionId,
    questionTitle,
    responseValue,
    sectionName
  ) => {
    try {
      // Set optimizing state for this question
      setOptimizing((prev) => ({ ...prev, [questionId]: true }));

      // Get the brand name using the helper function
      console.log("Getting brand name for single question optimization...");
      const brandName = getBrandName();

      if (brandName === "the brand") {
        console.warn(
          "Couldn't find specific brand name, using generic fallback."
        );
      }

      console.log(
        `Using brand name: "${brandName}" for optimization of question: "${questionTitle}"`
      );

      // Call the narrative service to optimize
      const optimized = await narrativeService.optimizeQuestionResponse(
        questionTitle,
        responseValue,
        brandName,
        sectionName
      );

      console.log("Optimization result:", optimized);

      // Update the optimized responses state
      setOptimizedResponses((prev) => ({
        ...prev,
        [questionId]: optimized,
      }));

      // Auto-show the optimized version
      setShowOptimized((prev) => ({
        ...prev,
        [questionId]: true,
      }));

      toast.success("Content optimized for display!");
    } catch (error) {
      console.error("Error optimizing response:", error);
      toast.error(`Failed to optimize content: ${error.message}`);
    } finally {
      setOptimizing((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  // Function to toggle between original and optimized display
  const toggleOptimizedDisplay = (questionId) => {
    setShowOptimized((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  // Function to optimize all questions in a section at once
  const optimizeAllInSection = async (sectionId, sectionTitle) => {
    try {
      toast.loading("Optimizing all questions in section...");

      // Get all questions in this section
      const section = questionnaire.sections.find((s) => s.id === sectionId);
      if (!section || !section.questions || section.questions.length === 0) {
        toast.dismiss();
        toast.error("No questions found in this section");
        return;
      }

      // Get the brand name using the helper function
      console.log(
        `Getting brand name for optimization of section: "${sectionTitle}"...`
      );
      const brandName = getBrandName();

      if (brandName === "the brand") {
        console.warn(
          "Couldn't find specific brand name, using generic fallback for section optimization."
        );
      }

      console.log(`Using brand name: "${brandName}" for section optimization`);

      // Set optimizing state for all questions in the section
      const optimizingState = { ...optimizing };
      section.questions.forEach((question) => {
        optimizingState[question.id] = true;
      });
      setOptimizing(optimizingState);

      // Process each question in sequence
      const newOptimizedResponses = { ...optimizedResponses };
      const newShowOptimized = { ...showOptimized };

      console.log(
        `Beginning optimization of ${section.questions.length} questions in section "${sectionTitle}"`
      );

      for (const question of section.questions) {
        const response = data?.responses?.[question.id];
        if (!response || !response.value) {
          console.log(
            `Skipping question "${question.title}" - no response value`
          );
          continue;
        }

        try {
          console.log(`Optimizing question: "${question.title}"`);
          const optimized = await narrativeService.optimizeQuestionResponse(
            question.title,
            response.value,
            brandName,
            sectionTitle
          );

          console.log(`Successfully optimized question: "${question.title}"`);
          newOptimizedResponses[question.id] = optimized;
          newShowOptimized[question.id] = true;
        } catch (error) {
          console.error(
            `Error optimizing question ${question.id} "${question.title}":`,
            error
          );
        }
      }

      // Update state with all optimized responses
      setOptimizedResponses(newOptimizedResponses);
      setShowOptimized(newShowOptimized);

      // Clear optimizing state
      const clearedOptimizingState = { ...optimizing };
      section.questions.forEach((question) => {
        clearedOptimizingState[question.id] = false;
      });
      setOptimizing(clearedOptimizingState);

      toast.dismiss();
      toast.success("All questions in section optimized!");
    } catch (error) {
      console.error("Error optimizing section:", error);
      toast.dismiss();
      toast.error(`Failed to optimize section: ${error.message}`);

      // Clear any pending optimizing states
      setOptimizing({});
    }
  };

  // Function to toggle using optimized content in the profile
  const toggleUseOptimizedInProfile = () => {
    setUseOptimizedInProfile((prev) => !prev);
  };

  // Handle optimized content edits
  const handleOptimizedEdit = (questionId, field, value) => {
    setOptimizedEdits((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        [field]: value || "",
        originalOptimized: optimizedResponses[questionId] || {},
      },
    }));
  };

  // Save optimized content edits
  const saveOptimizedEdit = (questionId) => {
    if (!optimizedEdits[questionId]) return;

    try {
      // Get the current optimized response
      const currentOptimized = optimizedResponses[questionId] || {};

      // Create updated optimized response with edited content
      const updatedOptimized = {
        ...currentOptimized,
        displayTitle:
          optimizedEdits[questionId].displayTitle !== undefined
            ? optimizedEdits[questionId].displayTitle
            : currentOptimized.displayTitle,
        displayContent:
          optimizedEdits[questionId].displayContent !== undefined
            ? optimizedEdits[questionId].displayContent
            : currentOptimized.displayContent,
        manuallyEdited: true,
        lastEditedAt: new Date(),
      };

      // Update the optimized responses state
      setOptimizedResponses((prev) => ({
        ...prev,
        [questionId]: updatedOptimized,
      }));

      // Clear the edits
      setOptimizedEdits((prev) => ({
        ...prev,
        [questionId]: undefined,
      }));

      toast.success("Optimized content updated!");
    } catch (error) {
      console.error("Error saving optimized edit:", error);
      toast.error("Failed to save optimized content edit");
    }
  };

  // Cancel optimized edit
  const cancelOptimizedEdit = (questionId) => {
    setOptimizedEdits((prev) => ({
      ...prev,
      [questionId]: undefined,
    }));
  };

  // Function to save optimized responses
  const saveOptimizedResponses = async () => {
    try {
      setSaving(true);

      // Get the current response to ensure we have the latest metadata
      const currentResponse = await responseService.getResponseById(responseId);
      if (!currentResponse) {
        throw new Error("Failed to retrieve current response data");
      }

      // Create a copy of the data with optimized responses
      const dataToSave = {
        ...currentResponse,
        metadata: {
          ...(currentResponse.metadata || {}),
          optimizedResponses: optimizedResponses,
          useOptimizedInProfile: useOptimizedInProfile,
        },
      };

      // Update the response in Firestore
      await responseService.updateResponse(responseId, dataToSave);

      // Update local data state to reflect the saved changes
      setData(dataToSave);

      toast.success("Optimized content saved!");
    } catch (error) {
      console.error("Error saving optimized responses:", error);
      toast.error("Failed to save optimized content");
    } finally {
      setSaving(false);
    }
  };

  // Helper to get a unique key for a field
  const getFieldKey = (type, ...indices) => {
    if (type === "soundboard.description") return "soundboard.description";
    if (type === "soundboard.notes") return `soundboard.notes.${indices[0]}`;
    if (type === "deliverySections.paragraphs")
      return `deliverySections.${indices[0]}.paragraphs.${indices[1]}`;
    return type;
  };

  // Optimizer handler for arbitrary fields
  const handleOptimizeField = async (key, label, value) => {
    try {
      setOptimizingContent((prev) => ({ ...prev, [key]: true }));
      const brandName = getBrandName();
      const optimized = await narrativeService.optimizeQuestionResponse(
        label,
        value,
        brandName,
        label // sectionName
      );
      setOptimizedContent((prev) => ({ ...prev, [key]: optimized }));
      setShowOptimizedContent((prev) => ({ ...prev, [key]: true }));
      toast.success("Content optimized for display!");
    } catch (error) {
      toast.error("Failed to optimize content");
    } finally {
      setOptimizingContent((prev) => ({ ...prev, [key]: false }));
    }
  };

  const toggleShowOptimizedField = (key) => {
    setShowOptimizedContent((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleOptimizedFieldEdit = (key, field, value) => {
    setOptimizedContentEdits((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
        originalOptimized: optimizedContent[key] || {},
      },
    }));
  };

  const saveOptimizedFieldEdit = (key) => {
    if (!optimizedContentEdits[key]) return;
    const currentOptimized = optimizedContent[key] || {};
    const updatedOptimized = {
      ...currentOptimized,
      displayTitle:
        optimizedContentEdits[key].displayTitle !== undefined
          ? optimizedContentEdits[key].displayTitle
          : currentOptimized.displayTitle,
      displayContent:
        optimizedContentEdits[key].displayContent !== undefined
          ? optimizedContentEdits[key].displayContent
          : currentOptimized.displayContent,
      manuallyEdited: true,
      lastEditedAt: new Date(),
    };
    setOptimizedContent((prev) => ({ ...prev, [key]: updatedOptimized }));
    setOptimizedContentEdits((prev) => ({ ...prev, [key]: undefined }));
    toast.success("Optimized content updated!");
  };

  const cancelOptimizedFieldEdit = (key) => {
    setOptimizedContentEdits((prev) => ({ ...prev, [key]: undefined }));
  };

  // Render a field based on question type
  const renderField = (question, response) => {
    const questionId = question.id;
    const value = response?.value || "";
    const visibilitySettings = visibility[questionId] || {
      visibleInEditor: true,
      visibleInProfile: true,
    };

    // Common props for all input types
    const commonProps = {
      id: `field-${questionId}`,
      value: value,
      onChange: (e) => updateResponseValue(questionId, e.target.value),
      className:
        "w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-white",
      disabled: saving,
    };

    // Render different input types based on question type
    switch (question.type) {
      case "text":
        return <input type="text" {...commonProps} />;
      case "textarea":
        return <textarea {...commonProps} rows={4} />;
      case "select":
        return (
          <select {...commonProps}>
            <option value="">Select an option</option>
            {question.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      case "radio":
        return (
          <div className="flex flex-col space-y-2">
            {question.options?.map((option) => (
              <label key={option.value} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={`radio-${questionId}`}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) =>
                    updateResponseValue(questionId, e.target.value)
                  }
                  disabled={saving}
                />
                <span className="text-white">{option.label}</span>
              </label>
            ))}
          </div>
        );
      case "checkbox":
        return (
          <div className="flex flex-col space-y-2">
            {question.options?.map((option) => (
              <label key={option.value} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  value={option.value}
                  checked={
                    Array.isArray(value) ? value.includes(option.value) : false
                  }
                  onChange={(e) => {
                    let newValue = Array.isArray(value) ? [...value] : [];
                    if (e.target.checked) {
                      newValue.push(option.value);
                    } else {
                      newValue = newValue.filter((val) => val !== option.value);
                    }
                    updateResponseValue(questionId, newValue);
                  }}
                  disabled={saving}
                />
                <span className="text-white">{option.label}</span>
              </label>
            ))}
          </div>
        );
      case "slider":
        return (
          <div className="flex flex-col space-y-2">
            <input
              type="range"
              min={question.min || 0}
              max={question.max || 100}
              step={question.step || 1}
              {...commonProps}
            />
            <span className="text-white text-center">{value}</span>
          </div>
        );
      case "scale_1_10":
        return (
          <ScaleSlider
            id={questionId}
            label={question.title}
            leftLabel={question.leftLabel}
            rightLabel={question.rightLabel}
            value={value || 5}
            onChange={(v) => updateResponseValue(questionId, v)}
            disabled={saving}
          />
        );
      default:
        return <input type="text" {...commonProps} />;
    }
  };

  // Show loading indicator
  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Show error message if there's an error
  if (error) {
    return (
      <div className="bg-red-500/10 p-6 rounded-xl border border-red-500/20">
        <h2 className="text-xl font-bold text-red-500 mb-2">Error</h2>
        <p className="text-white/70">{error}</p>
      </div>
    );
  }

  // Show empty state if no data
  if (!data || !questionnaire) {
    return (
      <div className="p-8">
        <div className="bg-gray-800 rounded-lg p-4">
          <p className="text-gray-400">No data available</p>
        </div>
      </div>
    );
  }

  // Handle audio/video upload for soundboard
  const handleAudioUpload = async (idx) => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    // Get current media type or default to audio
    const currentMediaType = data.soundboard?.audioFiles?.[idx]?.mediaType || "audio";
    fileInput.accept = currentMediaType === "video" ? "video/*" : "audio/*";

    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Validate the file type based on media type
      const isValidType = currentMediaType === "video" 
        ? file.type.startsWith("video/")
        : file.type.startsWith("audio/");
      
      if (!isValidType) {
        toast.error(`Please select a valid ${currentMediaType} file`);
        return;
      }

      // Check file size (100MB max)
      const MAX_FILE_SIZE = 100 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        toast.error(
          `File is too large. Maximum size is 100MB. Your file is ${(
            file.size /
            (1024 * 1024)
          ).toFixed(2)}MB`
        );
        return;
      }

      // Create a unique toast ID to update progress
      const toastId = toast.loading(`Preparing to upload ${currentMediaType} file...`);

      try {
        // Create a reference to Firebase Storage
        const storageRef = ref(storage, `${currentMediaType}/${Date.now()}_${file.name}`);

        // Create upload task
        const uploadTask = uploadBytesResumable(storageRef, file);

        // Listen for state changes
        uploadTask.on(
          "state_changed",
          // Progress observer
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            toast.loading(`Uploading: ${Math.round(progress)}%`, {
              id: toastId,
            });
          },
          // Error observer
          (error) => {
            console.error("Upload error:", error);
            toast.error("Upload failed: " + error.message, { id: toastId });
          },
          // Completion observer
          async () => {
            // Get the download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            // Update the audio/video file URL
            setData((prev) => {
              const newSoundboard = { ...prev.soundboard };
              const newAudioFiles = [...(newSoundboard.audioFiles || [])];
              newAudioFiles[idx] = {
                ...newAudioFiles[idx],
                url: downloadURL,
                mediaType: currentMediaType, // Ensure mediaType is set
              };
              newSoundboard.audioFiles = newAudioFiles;
              return { ...prev, soundboard: newSoundboard };
            });

            // If no title is set, use the filename (without extension)
            const currentTitle = data.soundboard?.audioFiles?.[idx]?.title;
            if (!currentTitle) {
              const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
              setData((prev) => {
                const newSoundboard = { ...prev.soundboard };
                const newAudioFiles = [...(newSoundboard.audioFiles || [])];
                newAudioFiles[idx] = {
                  ...newAudioFiles[idx],
                  title: fileNameWithoutExt,
                };
                newSoundboard.audioFiles = newAudioFiles;
                return { ...prev, soundboard: newSoundboard };
              });
            }

            toast.success(`${currentMediaType.charAt(0).toUpperCase() + currentMediaType.slice(1)} file uploaded successfully`, { id: toastId });
          }
        );
      } catch (error) {
        console.error("Error handling file upload:", error);
        toast.error("Upload failed: " + error.message, { id: toastId });
      }
    };

    fileInput.click();
  };

  // Handle delivery media upload
  const handleDeliveryMediaUpload = async (index, mediaType = "video") => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = mediaType === "video" ? "video/*" : "audio/*";

    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Validate the file type
      const validType =
        mediaType === "video"
          ? file.type.startsWith("video/")
          : file.type.startsWith("audio/");

      if (!validType) {
        toast.error(`Please select a valid ${mediaType} file`);
        return;
      }

      // Check file size (100MB max for video, 100MB for audio)
      const MAX_FILE_SIZE =
        mediaType === "video" ? 100 * 1024 * 1024 : 100 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        toast.error(
          `File is too large. Maximum size is ${
            mediaType === "video" ? "100MB" : "10MB"
          }. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`
        );
        return;
      }

      // Create a unique toast ID to update progress
      const toastId = toast.loading(`Preparing to upload ${mediaType} file...`);

      try {
        // Create a reference to Firebase Storage
        const storageRef = ref(
          storage,
          `${mediaType}/${Date.now()}_${file.name}`
        );

        // Create upload task
        const uploadTask = uploadBytesResumable(storageRef, file);

        // Listen for state changes
        uploadTask.on(
          "state_changed",
          // Progress observer
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            toast.loading(`Uploading: ${Math.round(progress)}%`, {
              id: toastId,
            });
          },
          // Error observer
          (error) => {
            console.error("Upload error:", error);
            toast.error("Upload failed: " + error.message, { id: toastId });
          },
          // Completion observer
          async () => {
            // Get the download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            // Update the section media URL
            setData((prev) => {
              const newSections = [...(prev.deliverySections || [])];
              newSections[index] = {
                ...newSections[index],
                mediaUrl: downloadURL,
                mediaType: mediaType,
              };
              return { ...prev, deliverySections: newSections };
            });

            // If no title is set, use the filename (without extension)
            const currentTitle = data.deliverySections?.[index]?.title;
            if (!currentTitle) {
              const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
              setData((prev) => {
                const newSections = [...(prev.deliverySections || [])];
                newSections[index] = {
                  ...newSections[index],
                  title: fileNameWithoutExt,
                };
                return { ...prev, deliverySections: newSections };
              });
            }

            toast.success(
              `${
                mediaType.charAt(0).toUpperCase() + mediaType.slice(1)
              } file uploaded successfully`,
              { id: toastId }
            );
          }
        );
      } catch (error) {
        console.error("Error handling file upload:", error);
        toast.error("Upload failed: " + error.message, { id: toastId });
      }
    };

    fileInput.click();
  };

  // Test media URL function
  const testMediaUrl = (url, mediaType = "video") => {
    if (!url.trim()) {
      toast.error(`Please enter a ${mediaType} URL to test`);
      return;
    }

    toast.loading(`Testing ${mediaType} URL...`);

    if (mediaType === "video") {
      // Create a video element to test the URL
      const video = document.createElement("video");

      // Listen for successful loading
      video.oncanplaythrough = () => {
        toast.dismiss();
        toast.success("Video file is valid and playable");
      };

      // Listen for errors
      video.onerror = () => {
        toast.dismiss();
        toast.error("The video URL is not accessible or valid");
      };

      // Set the source and load the video
      video.src = url;
      video.load();
    } else {
      // Create an audio element to test the URL
      const audio = new Audio(url);

      // Listen for successful loading
      audio.oncanplaythrough = () => {
        toast.dismiss();
        toast.success("Audio file is valid and playable");
      };

      // Listen for errors
      audio.onerror = () => {
        toast.dismiss();
        toast.error("The audio URL is not accessible or valid");
      };

      // Load the audio
      audio.load();
    }
  };

  // Add paragraph to delivery section
  const addParagraphToDeliverySection = (sectionIndex) => {
    setData((prev) => {
      const newSections = [...(prev.deliverySections || [])];
      const currentParagraphs = newSections[sectionIndex].paragraphs || [""];
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        paragraphs: [...currentParagraphs, ""],
      };
      return { ...prev, deliverySections: newSections };
    });
  };

  // Update paragraph in delivery section
  const updateParagraphInDeliverySection = (
    sectionIndex,
    paragraphIndex,
    value
  ) => {
    setData((prev) => {
      const newSections = [...(prev.deliverySections || [])];
      const currentParagraphs = [
        ...(newSections[sectionIndex].paragraphs || []),
      ];
      currentParagraphs[paragraphIndex] = value;
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        paragraphs: currentParagraphs,
      };
      return { ...prev, deliverySections: newSections };
    });
  };

  // Remove paragraph from delivery section
  const removeParagraphFromDeliverySection = (sectionIndex, paragraphIndex) => {
    setData((prev) => {
      const newSections = [...(prev.deliverySections || [])];
      const currentParagraphs = [
        ...(newSections[sectionIndex].paragraphs || []),
      ];
      currentParagraphs.splice(paragraphIndex, 1);
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        paragraphs: currentParagraphs,
      };
      return { ...prev, deliverySections: newSections };
    });
  };

  // Add a function to replace or update the existing code for handling audio file descriptions
  const handleAddDescriptionToAudio = (idx) => {
    setData((prev) => {
      const newAudio = [...(prev.soundboard?.audioFiles || [])];
      // Ensure the audio object has a descriptions array
      if (!Array.isArray(newAudio[idx].descriptions)) {
        // Convert from old format if needed
        newAudio[idx].descriptions = newAudio[idx].description
          ? [newAudio[idx].description]
          : [];
      }

      // Add a new empty description
      newAudio[idx].descriptions.push("");
      return {
        ...prev,
        soundboard: { ...prev.soundboard, audioFiles: newAudio },
      };
    });
  };

  const handleUpdateAudioDescription = (audioIdx, descIdx, value) => {
    setData((prev) => {
      const newAudio = [...(prev.soundboard?.audioFiles || [])];

      // Ensure descriptions array exists
      if (!Array.isArray(newAudio[audioIdx].descriptions)) {
        newAudio[audioIdx].descriptions = newAudio[audioIdx].description
          ? [newAudio[audioIdx].description]
          : [];
      }

      // Update the specific description
      newAudio[audioIdx].descriptions[descIdx] = value;

      // Also update the description field for backward compatibility
      if (descIdx === 0) {
        newAudio[audioIdx].description = value;
      }

      return {
        ...prev,
        soundboard: { ...prev.soundboard, audioFiles: newAudio },
      };
    });
  };

  const handleRemoveAudioDescription = (audioIdx, descIdx) => {
    setData((prev) => {
      const newAudio = [...(prev.soundboard?.audioFiles || [])];

      // Ensure descriptions array exists
      if (!Array.isArray(newAudio[audioIdx].descriptions)) {
        return prev; // Can't remove if there's no descriptions array
      }

      // Remove the specific description
      newAudio[audioIdx].descriptions = newAudio[audioIdx].descriptions.filter(
        (_, idx) => idx !== descIdx
      );

      // Update the description field for backward compatibility
      if (newAudio[audioIdx].descriptions.length > 0) {
        newAudio[audioIdx].description = newAudio[audioIdx].descriptions[0];
      } else {
        newAudio[audioIdx].description = "";
      }

      return {
        ...prev,
        soundboard: { ...prev.soundboard, audioFiles: newAudio },
      };
    });
  };

  // Helper function to get brand name from responses
  const getBrandName = () => {
    try {
      console.log("Beginning getBrandName search...");

      // Common patterns for brand/company name fields
      const brandNamePatterns = [
        "brand name",
        "company name",
        "business name",
        "organization name",
        "name of brand",
        "name of company",
        "name of business",
        "brand/company name",
      ];

      // Check if question title matches any of our patterns
      const matchesNamePattern = (title) => {
        if (!title) return false;
        const lowerTitle = title.toLowerCase();
        return brandNamePatterns.some((pattern) =>
          lowerTitle.includes(pattern)
        );
      };

      // APPROACH 1: First try to find Brand/Company name in the questionnaire structure
      const brandSection = questionnaire?.sections.find(
        (section) =>
          section.title.toLowerCase().includes("information") ||
          section.title.toLowerCase().includes("info") ||
          section.title.toLowerCase().includes("brand") ||
          section.title.toLowerCase().includes("company") ||
          section.title.toLowerCase().includes("about")
      );

      console.log("Found brand section in questionnaire?", !!brandSection);
      if (brandSection) {
        console.log("Brand section title:", brandSection.title);
        console.log("Brand section questions:", brandSection.questions.length);

        // Look for any questions that match our brand name patterns
        const possibleNameQuestions = brandSection.questions.filter((q) =>
          matchesNamePattern(q.title)
        );

        // If we have matches, log them
        if (possibleNameQuestions.length > 0) {
          console.log(
            "Possible name questions found:",
            possibleNameQuestions.map((q) => q.title)
          );

          // Try to find one with a response
          for (const question of possibleNameQuestions) {
            if (data?.responses?.[question.id]?.value) {
              const brandName = data.responses[question.id].value;
              console.log(
                "Found brand name via specific pattern match:",
                brandName
              );
              return brandName;
            }
          }
        }

        // Fallback to any question with "name" in it
        const nameQuestions = brandSection.questions.filter(
          (q) => q.title && q.title.toLowerCase().includes("name")
        );

        if (nameQuestions.length > 0) {
          console.log(
            "Questions with 'name' in title:",
            nameQuestions.map((q) => q.title)
          );

          // Try to find one with a response
          for (const question of nameQuestions) {
            if (data?.responses?.[question.id]?.value) {
              const brandName = data.responses[question.id].value;
              console.log("Found brand name via 'name' in title:", brandName);
              return brandName;
            }
          }
        }
      }

      // APPROACH 2: Directly search all responses for matches to name patterns
      if (data?.responses) {
        console.log("Searching all responses for name pattern matches...");
        const allQuestions =
          questionnaire?.sections.flatMap((s) => s.questions) || [];

        // First try exact pattern matches
        for (const pattern of brandNamePatterns) {
          for (const [questionId, response] of Object.entries(data.responses)) {
            if (!response.value) continue;

            const question = allQuestions.find((q) => q.id === questionId);
            if (!question?.title) continue;

            if (question.title.toLowerCase().includes(pattern)) {
              console.log(
                `Found pattern "${pattern}" match in question: "${question.title}"`
              );
              console.log("Response value:", response.value);
              return response.value;
            }
          }
        }

        // Then try any question with "name"
        for (const [questionId, response] of Object.entries(data.responses)) {
          if (!response.value) continue;

          const question = allQuestions.find((q) => q.id === questionId);
          if (!question?.title) continue;

          const title = question.title.toLowerCase();
          if (
            title.includes("name") &&
            (title.includes("brand") ||
              title.includes("company") ||
              title.includes("business"))
          ) {
            console.log(
              `Found name+brand/company in question: "${question.title}"`
            );
            console.log("Response value:", response.value);
            return response.value;
          }
        }
      }

      // APPROACH 3: Try to find a brand info section in data.data
      if (data?.data) {
        console.log("Searching in data.data structure...");

        // Find sections that might contain brand info
        const brandInfoSections = Object.values(data.data).filter((section) => {
          const sectionTitle = section.sectionTitle || "";
          return (
            sectionTitle.toLowerCase().includes("information") ||
            sectionTitle.toLowerCase().includes("info") ||
            sectionTitle.toLowerCase().includes("brand") ||
            sectionTitle.toLowerCase().includes("company") ||
            sectionTitle.toLowerCase().includes("about")
          );
        });

        console.log(
          "Potential brand info sections found:",
          brandInfoSections.length
        );

        // Go through each potential section
        for (const section of brandInfoSections) {
          if (!section.responses) continue;

          console.log("Checking section:", section.sectionTitle);
          console.log(
            "Questions in section:",
            Object.keys(section.responses).length
          );

          // First try matching patterns
          for (const pattern of brandNamePatterns) {
            const nameResponse = Object.values(section.responses).find((q) =>
              (q.questionText || "").toLowerCase().includes(pattern)
            );

            if (nameResponse?.value) {
              console.log(`Found pattern "${pattern}" match in data.data`);
              console.log("Response value:", nameResponse.value);
              return nameResponse.value;
            }
          }

          // Then try any question with "name"
          const nameResponse = Object.values(section.responses).find((q) =>
            (q.questionText || "").toLowerCase().includes("name")
          );

          if (nameResponse?.value) {
            console.log("Found 'name' in question text from data.data");
            console.log("Response value:", nameResponse.value);
            return nameResponse.value;
          }
        }
      }

      // APPROACH 4: Final attempt - look for any reasonable name-like response
      if (data?.responses) {
        console.log("Trying final keyword matching approach...");
        const allQuestions =
          questionnaire?.sections.flatMap((s) => s.questions) || [];

        // Create a scoring system for questions that might contain a brand name
        const scoredResponses = Object.entries(data.responses)
          .map(([questionId, response]) => {
            if (!response.value || typeof response.value !== "string")
              return null;

            const question = allQuestions.find((q) => q.id === questionId);
            if (!question) return null;

            const title = (question.title || "").toLowerCase();
            let score = 0;

            // Score based on keywords in question title
            if (title.includes("brand") && title.includes("name")) score += 10;
            if (title.includes("company") && title.includes("name"))
              score += 10;
            if (title.includes("business") && title.includes("name"))
              score += 10;
            if (title.includes("name")) score += 5;
            if (title.includes("brand")) score += 3;
            if (title.includes("company")) score += 3;
            if (title.includes("organization")) score += 3;
            if (title.includes("identity")) score += 2;

            // Score based on length and format of response (brand names are typically short)
            const value = response.value.trim();
            if (value.length > 2 && value.length < 30) score += 2;
            if (value.split(" ").length <= 4) score += 2; // Most brand names are 1-4 words

            return { questionId, question, response, score };
          })
          .filter(Boolean)
          .sort((a, b) => b.score - a.score); // Sort by highest score

        if (scoredResponses.length > 0) {
          const topCandidate = scoredResponses[0];
          console.log("Top brand name candidate:");
          console.log("- Question:", topCandidate.question.title);
          console.log("- Score:", topCandidate.score);
          console.log("- Value:", topCandidate.response.value);

          // If we have a reasonably good score, use this value
          if (topCandidate.score >= 5) {
            return topCandidate.response.value;
          }
        }
      }

      // Default fallback
      console.log("Could not find brand name, using default: 'the brand'");
      return "the brand";
    } catch (error) {
      console.error("Error getting brand name:", error);
      return "the brand";
    }
  };

  const togglePageVisibility = async (page) => {
    const newPages = { ...visibilityPages, [page]: !visibilityPages[page] };
    setVisibilityPages(newPages);
    try {
      await responseService.updateResponse(responseId, {
        visibility: { pages: newPages },
      });
    } catch (e) {
      console.error("Failed to update visibility", e);
    }
  };

  const toggleSectionVisibility = async (sectionId) => {
    const newSections = {
      ...visibilitySections,
      [sectionId]: !visibilitySections[sectionId],
    };
    setVisibilitySections(newSections);
    try {
      await responseService.updateResponse(responseId, {
        visibility: {
          pages: visibilityPages,
          sections: newSections,
        },
      });
    } catch (e) {
      console.error("Failed to update section visibility", e);
    }
  };

  return (
    <div className="dynamic-profile-editor">
      {/* Header with controls */}
      <div className="sticky top-0 z-20 bg-gray-900 border-b border-gray-800 mb-6 pb-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold text-white">Edit Profile Data</h2>
            <p className="text-gray-400 text-sm">Response ID: {responseId}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            {/* Default tab selector */}
            <div className="flex items-center bg-gray-800 rounded px-2 py-1 border border-gray-700">
              <span className="text-xs text-gray-300 mr-2">Default Tab</span>
              <select
                value={defaultTab}
                onChange={(e) => setDefaultTab(e.target.value)}
                className="bg-gray-900 text-white text-xs rounded px-2 py-1 border border-gray-700"
              >
                <option value="discovery">Discovery</option>
                <option value="soundboard">Soundboard</option>
                <option value="delivery">Delivery</option>
              </select>
            </div>
            {/* Theme color pickers */}
            <div className="flex items-center bg-gray-800 rounded px-2 py-1 border border-gray-700 gap-2">
              <span className="text-xs text-gray-300">Theme</span>
              <label className="flex items-center gap-1 text-xs text-gray-300">
                <span>Primary</span>
                <input
                  type="color"
                  value={theme.primary}
                  onChange={(e) =>
                    setTheme((t) => ({ ...t, primary: e.target.value }))
                  }
                  className="w-6 h-6 p-0 border-0 bg-transparent"
                  title="Primary color"
                />
              </label>
              <label className="flex items-center gap-1 text-xs text-gray-300">
                <span>Secondary</span>
                <input
                  type="color"
                  value={theme.secondary}
                  onChange={(e) =>
                    setTheme((t) => ({ ...t, secondary: e.target.value }))
                  }
                  className="w-6 h-6 p-0 border-0 bg-transparent"
                  title="Secondary color"
                />
              </label>
              <label className="flex items-center gap-1 text-xs text-gray-300">
                <span>Accent</span>
                <input
                  type="color"
                  value={theme.accent}
                  onChange={(e) =>
                    setTheme((t) => ({ ...t, accent: e.target.value }))
                  }
                  className="w-6 h-6 p-0 border-0 bg-transparent"
                  title="Accent color"
                />
              </label>
            </div>
            {Object.keys(optimizedResponses).length > 0 && (
              <button
                onClick={toggleUseOptimizedInProfile}
                className={`flex items-center px-4 py-2 ${
                  useOptimizedInProfile
                    ? "bg-purple-600 hover:bg-purple-700"
                    : "bg-gray-600 hover:bg-gray-700"
                } rounded text-white`}
                disabled={saving}
              >
                <Sparkles className="mr-2" size={18} />
                {useOptimizedInProfile
                  ? "Optimized Content On"
                  : "Optimized Content Off"}
              </button>
            )}
            {/* Page visibility toggles */}
            {["discovery", "soundboard", "delivery"].map((p) => (
              <button
                key={p}
                onClick={() => togglePageVisibility(p)}
                className={`flex items-center gap-1 px-3 py-2 rounded text-sm mr-2 ${
                  visibilityPages[p]
                    ? "bg-green-900/30 text-green-300"
                    : "bg-gray-700 text-gray-300"
                }`}
                title={`${visibilityPages[p] ? "Hide " : "Show "}${p}`}
              >
                {visibilityPages[p] ? <Eye size={14} /> : <EyeOff size={14} />}
                <span className="capitalize hidden md:inline">{p}</span>
              </button>
            ))}

            <button
              onClick={openFullView}
              className="flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded text-white"
              disabled={saving}
            >
              <ExternalLink className="mr-2" size={18} />
              Open Full Page
            </button>
            <button
              onClick={handleSave}
              className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 rounded text-white"
              disabled={saving}
            >
              <Save className="mr-2" size={18} />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* Custom URL Name Input */}
        <div className="mt-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
          <h3 className="text-white font-medium mb-2 flex items-center">
            <LinkIcon size={18} className="mr-2" />
            Custom Profile URL
          </h3>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-grow">
              <input
                type="text"
                value={customUrlName}
                onChange={handleCustomUrlChange}
                placeholder="Enter a name for your profile URL"
                className={`w-full bg-gray-800 border ${
                  urlExists ? "border-red-500" : "border-gray-700"
                } rounded-md p-2 text-white`}
                disabled={saving}
              />
              {urlExists && (
                <p className="text-red-400 text-xs mt-1">
                  This URL is already in use. Please choose a different name.
                </p>
              )}
              <p className="text-gray-400 text-xs mt-1">
                This will be used in the URL for sharing your brand profile.
              </p>
            </div>
            <div className="flex-shrink-0 md:w-1/3">
              <div className="bg-gray-900 border border-gray-700 rounded-md p-2 text-gray-300 text-sm overflow-hidden overflow-ellipsis whitespace-nowrap">
                /brand-page/{customUrlPreview || "brand-profile"}
              </div>
              <p className="text-gray-400 text-xs mt-1">URL preview</p>
            </div>
          </div>
        </div>

        {/* Google Drive URL Input */}
        <div className="mt-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
          <h3 className="text-white font-medium mb-2 flex items-center">
            <ExternalLink size={18} className="mr-2" />
            Delivery Google Drive Folder
          </h3>
          <div className="flex flex-col gap-3">
            <div className="flex-grow">
              <input
                type="url"
                value={deliveryDriveUrl}
                onChange={(e) => setDeliveryDriveUrl(e.target.value)}
                placeholder="https://drive.google.com/drive/folders/..."
                className="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-white"
                disabled={saving}
              />
              <p className="text-gray-400 text-xs mt-1">
                Enter the Google Drive folder URL for delivery assets. This will replace the ZIP download button.
              </p>
            </div>
          </div>
        </div>

        {/* Optimized Content Info (Show when optimized content exists) */}
        {Object.keys(optimizedResponses).length > 0 && (
          <div className="mt-4 p-4 bg-purple-900/20 rounded-lg border border-purple-800/30">
            <h3 className="text-white font-medium mb-2 flex items-center">
              <Sparkles size={18} className="mr-2 text-purple-400" />
              Optimized Content Display
            </h3>
            <p className="text-gray-300 text-sm mb-3">
              AI-optimized content improves how your brand profile appears to
              viewers.
              {useOptimizedInProfile
                ? " Currently enabled for public profile."
                : " Currently disabled for public profile."}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={toggleUseOptimizedInProfile}
                className={`flex items-center px-3 py-1.5 rounded text-sm ${
                  useOptimizedInProfile
                    ? "bg-purple-600 hover:bg-purple-700 text-white"
                    : "bg-gray-700 hover:bg-gray-600 text-gray-200"
                }`}
              >
                {useOptimizedInProfile ? (
                  <>
                    <Check size={14} className="mr-1" />
                    Using Optimized Content
                  </>
                ) : (
                  <>
                    <Edit size={14} className="mr-1" />
                    Use Original Content
                  </>
                )}
              </button>
              <button
                onClick={saveOptimizedResponses}
                className="flex items-center px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded text-white text-sm"
                disabled={saving}
              >
                <Save size={14} className="mr-1" />
                Save Display Settings
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Brand Images Management Section - Moved to top for priority */}
      <div className="mt-6 border border-gray-700 rounded-lg overflow-hidden mb-8 bg-gray-900/50">
        <div
          className="flex justify-between items-center bg-gray-800/80 p-4 cursor-pointer"
          onClick={() =>
            setExpandedSections((prev) => ({
              ...prev,
              brandImages: !prev.brandImages,
            }))
          }
        >
          <div className="flex items-center">
            <span className="mr-2 text-blue-400">🖼️</span>
            <h3 className="text-white font-medium">Brand Images</h3>
            <span className="ml-2 text-xs text-gray-400">
              (Logo, Hero Images & Backgrounds)
            </span>
          </div>
          <ChevronDown
            className={`transform transition-transform text-white/70 ${
              expandedSections.brandImages ? "rotate-180" : ""
            }`}
            size={20}
          />
        </div>
        {expandedSections.brandImages && (
          <div className="p-6 space-y-8">
            {/* Active Hero Image Display - Prominent at the top */}
            {brandImages.hero.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-white flex items-center">
                  <span className="mr-2">⭐</span>
                  Active Hero Image
                </h4>
                <p className="text-sm text-gray-400">
                  This image will be prominently displayed in your brand profile
                  header.
                </p>

                <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-2 border-blue-500/30 rounded-xl p-6">
                  {getActiveHeroImage() ? (
                    <div className="text-center">
                      <div className="relative inline-block mb-4">
                        <img
                          src={getActiveHeroImage().url}
                          alt={getActiveHeroImage().name}
                          className="w-48 h-32 object-cover rounded-lg border-2 border-blue-400 shadow-lg"
                        />
                        <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                          ACTIVE
                        </div>
                      </div>
                      <h5 className="text-lg font-medium text-white mb-2">
                        {getActiveHeroImage().name}
                      </h5>
                      <p className="text-blue-300 text-sm">
                        This is your primary hero image that visitors will see
                        first.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto bg-gray-700 rounded-lg flex items-center justify-center mb-4">
                        <span className="text-gray-400 text-2xl">⭐</span>
                      </div>
                      <p className="text-gray-400">
                        No active hero image selected
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Hero Images Section with Selection */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white flex items-center">
                <span className="mr-2">🌟</span>
                Hero Images
              </h4>
              <p className="text-sm text-gray-400">
                Upload and manage hero images. Click on any image to set it as
                the active hero image.
              </p>

              <div className="border border-gray-600 rounded-lg p-4 bg-gray-800/40">
                {brandImages.hero.length > 0 ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {brandImages.hero.map((image, index) => (
                        <div key={image.id} className="relative group">
                          <div
                            className={`relative cursor-pointer transition-all duration-200 ${
                              image.id === activeHeroImageId
                                ? "ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-800"
                                : "hover:ring-2 hover:ring-gray-400 hover:ring-offset-2 hover:ring-offset-gray-800"
                            }`}
                            onClick={() => setActiveHeroImage(image.id)}
                          >
                            <img
                              src={image.url}
                              alt={image.name}
                              className="w-full h-24 object-cover rounded-lg border border-gray-600"
                            />
                            {image.id === activeHeroImageId && (
                              <div className="absolute inset-0 bg-blue-500/20 rounded-lg flex items-center justify-center">
                                <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                                  ACTIVE
                                </div>
                              </div>
                            )}
                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImageDelete("hero", image.id);
                                }}
                                className="p-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-400 mt-1 truncate text-center">
                            {image.name}
                          </p>
                          {image.id !== activeHeroImageId && (
                            <button
                              onClick={() => setActiveHeroImage(image.id)}
                              className="w-full mt-1 px-2 py-1 text-xs bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 rounded transition-colors"
                            >
                              Set as Active
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => triggerImageUpload("hero")}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                    >
                      Add More Hero Images
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto bg-gray-700 rounded-lg flex items-center justify-center mb-4">
                      <span className="text-gray-400 text-2xl">🌟</span>
                    </div>
                    <p className="text-gray-400 mb-4">
                      No hero images uploaded
                    </p>
                    <button
                      onClick={() => triggerImageUpload("hero")}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                    >
                      Upload Hero Images
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Logo Section */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white flex items-center">
                <span className="mr-2">🏷️</span>
                Logo
              </h4>
              <p className="text-sm text-gray-400">
                Primary brand logo for profile headers and branding elements.
              </p>

              <div className="border border-gray-600 rounded-lg p-4 bg-gray-800/40">
                {brandImages.logo ? (
                  <div className="flex items-center gap-4">
                    <div className="relative group">
                      <img
                        src={brandImages.logo}
                        alt="Logo"
                        className="w-20 h-20 object-contain bg-white/5 rounded-lg border border-gray-600"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <button
                          onClick={() => handleImageDelete("logo")}
                          className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">Logo uploaded</p>
                      <button
                        onClick={() => triggerImageUpload("logo")}
                        className="mt-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                      >
                        Replace Logo
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto bg-gray-700 rounded-lg flex items-center justify-center mb-4">
                      <span className="text-gray-400 text-2xl">🏷️</span>
                    </div>
                    <p className="text-gray-400 mb-4">No logo uploaded</p>
                    <button
                      onClick={() => triggerImageUpload("logo")}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      Upload Logo
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Soundboard Images Section - Collapsed by default */}
            <details className="space-y-4">
              <summary className="text-lg font-semibold text-white flex items-center cursor-pointer hover:text-blue-400 transition-colors">
                <span className="mr-2">🎵</span>
                Soundboard Background Images
                <span className="ml-2 text-xs text-gray-500">
                  (Click to expand)
                </span>
              </summary>
              <div className="ml-6 space-y-4">
                <p className="text-sm text-gray-400">
                  Background images specifically for the soundboard section.
                </p>

                <div className="border border-gray-600 rounded-lg p-4 bg-gray-800/40">
                  {brandImages.soundboard.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {brandImages.soundboard.map((image, index) => (
                          <div key={image.id} className="relative group">
                            <img
                              src={image.url}
                              alt={image.name}
                              className="w-full h-24 object-cover rounded-lg border border-gray-600"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <button
                                onClick={() =>
                                  handleImageDelete("soundboard", image.id)
                                }
                                className="p-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                              >
                                <X size={14} />
                              </button>
                            </div>
                            <p className="text-xs text-gray-400 mt-1 truncate">
                              {image.name}
                            </p>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => triggerImageUpload("soundboard")}
                        className="w-full px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                      >
                        Add More Soundboard Images
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto bg-gray-700 rounded-lg flex items-center justify-center mb-4">
                        <span className="text-gray-400 text-2xl">🎵</span>
                      </div>
                      <p className="text-gray-400 mb-4">
                        No soundboard images uploaded
                      </p>
                      <button
                        onClick={() => triggerImageUpload("soundboard")}
                        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                      >
                        Upload Soundboard Images
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </details>

            {/* Delivery Background Images Section - Collapsed by default */}
            <details className="space-y-4">
              <summary className="text-lg font-semibold text-white flex items-center cursor-pointer hover:text-green-400 transition-colors">
                <span className="mr-2">🚚</span>
                Delivery Background Images
                <span className="ml-2 text-xs text-gray-500">
                  (Click to expand)
                </span>
              </summary>
              <div className="ml-6 space-y-4">
                <p className="text-sm text-gray-400">
                  Background images specifically for the delivery section.
                </p>

                <div className="border border-gray-600 rounded-lg p-4 bg-gray-800/40">
                  {brandImages.delivery && brandImages.delivery.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {brandImages.delivery.map((image, index) => (
                          <div key={image.id} className="relative group">
                            <img
                              src={image.url}
                              alt={image.name}
                              className="w-full h-24 object-cover rounded-lg border border-gray-600"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <button
                                onClick={() =>
                                  handleImageDelete("delivery", image.id)
                                }
                                className="p-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                              >
                                <X size={14} />
                              </button>
                            </div>
                            <p className="text-xs text-gray-400 mt-1 truncate">
                              {image.name}
                            </p>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => triggerImageUpload("delivery")}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                      >
                        Add More Delivery Images
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto bg-gray-700 rounded-lg flex items-center justify-center mb-4">
                        <span className="text-gray-400 text-2xl">🚚</span>
                      </div>
                      <p className="text-gray-400 mb-4">
                        No delivery background images uploaded
                      </p>
                      <button
                        onClick={() => triggerImageUpload("delivery")}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                      >
                        Upload Delivery Images
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </details>

            {/* Upload Progress Display */}
            {Object.keys(uploadingImages).some(
              (key) => uploadingImages[key]
            ) && (
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white">
                  Upload Progress
                </h4>
                {Object.entries(uploadingImages).map(
                  ([uploadId, isUploading]) =>
                    isUploading && (
                      <div
                        key={uploadId}
                        className="border border-gray-600 rounded-lg p-4 bg-gray-800/40"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white text-sm">
                            Uploading {uploadId}...
                          </span>
                          <span className="text-gray-400 text-sm">
                            {Math.round(imageUploadProgress[uploadId] || 0)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${imageUploadProgress[uploadId] || 0}%`,
                            }}
                          />
                        </div>
                      </div>
                    )
                )}
              </div>
            )}

            {/* Usage Instructions */}
            <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4">
              <h5 className="text-blue-400 font-medium mb-2">
                📝 Image Usage Guidelines
              </h5>
              <ul className="text-blue-300 text-sm space-y-1">
                <li>
                  • <strong>Active Hero Image:</strong> The main image displayed
                  in your profile header
                </li>
                <li>
                  • <strong>Hero Images:</strong> Click any hero image to set it
                  as active
                </li>
                <li>
                  • <strong>Logo:</strong> Used in profile headers and branding
                  elements
                </li>
                <li>
                  • <strong>Background Images:</strong> Rotate as backgrounds
                  for different profile sections
                </li>
                <li>
                  • <strong>Soundboard Images:</strong> Specific backgrounds for
                  audio/soundboard sections
                </li>
                <li>
                  • Images are automatically optimized and randomly selected for
                  sections
                </li>
                <li>
                  • Recommended: Upload multiple background images for visual
                  variety
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Editor Form */}
      <div className="space-y-8">
        {/* Content sections */}
        {(
          data?.questionnaireSnapshot?.sections ||
          questionnaire?.sections ||
          []
        ).map((section) => {
          // Determine which narrative section this corresponds to
          let narrativeSection = "";
          if (section.title.toLowerCase().includes("background"))
            narrativeSection = "brandBackground";
          else if (section.title.toLowerCase().includes("characteristic"))
            narrativeSection = "brandCharacteristics";
          else if (section.title.toLowerCase().includes("personality"))
            narrativeSection = "brandPersonality";
          else if (section.title.toLowerCase().includes("strategy"))
            narrativeSection = "brandStrategy";
          else if (section.title.toLowerCase().includes("demographic"))
            narrativeSection = "demographics";
          else if (section.title.toLowerCase().includes("sound"))
            narrativeSection = "brandSound";
          else if (section.title.toLowerCase().includes("goals"))
            narrativeSection = "projectGoals";
          else if (
            section.title.toLowerCase().includes("information") ||
            section.title.toLowerCase().includes("info")
          )
            narrativeSection = "brandInfo";

          // Get the section data by collecting all responses for this section
          const sectionData = {};
          if (data?.responses) {
            section.questions.forEach((question) => {
              const response = data.responses[question.id];
              if (response && response.value) {
                sectionData[question.title] = response.value;
              }
            });
          }

          return (
            <div key={section.id}>
              <div className="border border-gray-700 rounded-lg overflow-hidden mb-4">
                {/* Section header */}
                <div
                  className="flex justify-between items-center bg-gray-800 p-4 cursor-pointer"
                  onClick={() => toggleSection(section.id)}
                >
                  <h3 className="text-white font-medium">{section.title}</h3>
                  <div className="flex items-center space-x-2">
                    {/* Only show Optimize All button if this is not the Brand Info section */}
                    {narrativeSection !== "brandInfo" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent section toggle
                          optimizeAllInSection(section.id, section.title);
                        }}
                        className="text-xs px-3 py-1 bg-purple-600/30 hover:bg-purple-600/50 rounded text-purple-300 flex items-center"
                        disabled={saving}
                        title="Optimize all questions in this section"
                      >
                        <Sparkles size={14} className="mr-1" />
                        Optimize All
                      </button>
                    )}
                    <ChevronDown
                      className={`transform transition-transform text-white/70 ${
                        expandedSections[section.id] ? "rotate-180" : ""
                      }`}
                      size={20}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Use narrativeSection (visualizer key) instead of section.id
                        toggleSectionVisibility(narrativeSection || section.id);
                      }}
                      className={`p-1 rounded ${
                        visibilitySections[narrativeSection || section.id] === false
                          ? "text-gray-400 hover:text-gray-200"
                          : "text-green-400 hover:text-green-200"
                      }`}
                      title={
                        visibilitySections[narrativeSection || section.id] === false
                          ? "Show Section"
                          : "Hide Section"
                      }
                    >
                      {visibilitySections[narrativeSection || section.id] === false ? (
                        <EyeOff size={14} />
                      ) : (
                        <Eye size={14} />
                      )}
                    </button>
                  </div>
                </div>

                {/* Section content */}
                {expandedSections[section.id] && (
                  <div className="p-4 space-y-4">
                    {section.questions.map((question) => {
                      const response = data?.responses?.[question.id];
                      const visibilitySettings = visibility[question.id] || {
                        visibleInEditor: true,
                        visibleInProfile: true,
                      };
                      const isOptimized = !!optimizedResponses[question.id];
                      const isOptimizing = !!optimizing[question.id];
                      const showOptimizedContent = showOptimized[question.id];
                      const isBrandInfoSection =
                        narrativeSection === "brandInfo";

                      return (
                        <div
                          key={question.id}
                          className="space-y-2 border-b border-gray-700/30 pb-4 mb-2"
                        >
                          {/* Question Title/Text */}
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              {question.type === "scale_1_10" ? (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={
                                        questionTitles[question.id] !== undefined
                                          ? questionTitles[question.id]
                                          : question.title
                                      }
                                      onChange={(e) =>
                                        updateQuestionTitle(
                                          question.id,
                                          e.target.value
                                        )
                                      }
                                      className="flex-1 bg-gray-800 border border-gray-700 rounded-md p-2 text-white text-sm"
                                      placeholder="Question title"
                                      disabled={saving}
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        toggleQuestionTitleVisibility(question.id)
                                      }
                                      className={`px-2 py-1 rounded text-xs ${
                                        questionTitles[question.id] === ""
                                          ? "bg-red-600/20 text-red-400"
                                          : "bg-gray-600/20 text-gray-400"
                                      }`}
                                      title={
                                        questionTitles[question.id] === ""
                                          ? "Title hidden - click to show"
                                          : "Click to hide title"
                                      }
                                    >
                                      {questionTitles[question.id] === "" ? (
                                        <EyeOff size={12} />
                                      ) : (
                                        <Eye size={12} />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <label
                                  htmlFor={`field-${question.id}`}
                                  className="block text-white font-medium"
                                >
                                  {showOptimizedContent && isOptimized
                                    ? optimizedResponses[question.id]
                                        .displayTitle
                                    : question.title}
                                </label>
                              )}
                            </div>

                            <div className="flex gap-2">
                              {isOptimized && !isBrandInfoSection && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleOptimizedDisplay(question.id)
                                  }
                                  className={`text-xs px-2 py-1 rounded flex items-center ${
                                    showOptimizedContent
                                      ? "bg-purple-600/20 text-purple-400"
                                      : "bg-gray-600/20 text-gray-400"
                                  }`}
                                  title={
                                    showOptimizedContent
                                      ? "Showing optimized content"
                                      : "Showing original content"
                                  }
                                >
                                  {showOptimizedContent ? (
                                    <>
                                      <Sparkles size={12} className="mr-1" />{" "}
                                      Optimized
                                    </>
                                  ) : (
                                    <>
                                      <Edit size={12} className="mr-1" />{" "}
                                      Original
                                    </>
                                  )}
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() =>
                                  toggleVisibility(
                                    question.id,
                                    "visibleInProfile"
                                  )
                                }
                                className={`text-xs px-2 py-1 rounded flex items-center ${
                                  visibilitySettings.visibleInProfile
                                    ? "bg-green-600/20 text-green-400"
                                    : "bg-red-600/20 text-red-400"
                                }`}
                                title={
                                  visibilitySettings.visibleInProfile
                                    ? "Visible in profile"
                                    : "Hidden in profile"
                                }
                              >
                                {visibilitySettings.visibleInProfile ? (
                                  <>
                                    <Eye size={12} className="mr-1" /> Public
                                  </>
                                ) : (
                                  <>
                                    <EyeOff size={12} className="mr-1" /> Hidden
                                  </>
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Response Content */}
                          <div className="mt-2">
                            {showOptimizedContent &&
                            isOptimized &&
                            !isBrandInfoSection ? (
                              <div className="bg-purple-900/20 border border-purple-800/30 rounded-md p-3 text-white">
                                {optimizedEdits[question.id] ? (
                                  <div className="space-y-4">
                                    {/* Optimized Title Edit */}
                                    <div>
                                      <label className="block text-xs text-gray-400 mb-1">
                                        Optimized Title
                                      </label>
                                      <input
                                        type="text"
                                        value={
                                          optimizedEdits[question.id]
                                            .displayTitle ||
                                          optimizedResponses[question.id]
                                            .displayTitle
                                        }
                                        onChange={(e) =>
                                          handleOptimizedEdit(
                                            question.id,
                                            "displayTitle",
                                            e.target.value
                                          )
                                        }
                                        className="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-white"
                                        placeholder="Edit optimized title..."
                                      />
                                    </div>

                                    {/* Optimized Content Edit */}
                                    <div>
                                      <label className="block text-xs text-gray-400 mb-1">
                                        Optimized Content
                                      </label>
                                      <textarea
                                        value={
                                          optimizedEdits[question.id]
                                            .displayContent ||
                                          optimizedResponses[question.id]
                                            .displayContent
                                        }
                                        onChange={(e) =>
                                          handleOptimizedEdit(
                                            question.id,
                                            "displayContent",
                                            e.target.value
                                          )
                                        }
                                        className="w-full min-h-[100px] bg-gray-800 border border-gray-700 rounded-md p-2 text-white"
                                        placeholder="Edit optimized content..."
                                      />
                                    </div>

                                    {/* Save/Cancel Buttons */}
                                    <div className="flex justify-end gap-2">
                                      <button
                                        onClick={() =>
                                          cancelOptimizedEdit(question.id)
                                        }
                                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-xs"
                                        disabled={saving}
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() =>
                                          saveOptimizedEdit(question.id)
                                        }
                                        className="px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded text-white text-xs flex items-center"
                                        disabled={saving}
                                      >
                                        <Save size={12} className="mr-1" />
                                        Save
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <p className="whitespace-pre-line">
                                      {
                                        optimizedResponses[question.id]
                                          .displayContent
                                      }
                                    </p>
                                    <div className="flex justify-between mt-2">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleOptimizedEdit(
                                            question.id,
                                            "displayContent",
                                            optimizedResponses[question.id]
                                              .displayContent
                                          )
                                        }
                                        className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white flex items-center"
                                      >
                                        <Edit size={12} className="mr-1" />
                                        Edit Optimized
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          toggleOptimizedDisplay(question.id)
                                        }
                                        className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white"
                                      >
                                        Show Original
                                      </button>
                                    </div>
                                    {optimizedResponses[question.id]
                                      .manuallyEdited && (
                                      <div className="text-xs text-purple-300 mt-2 flex items-center">
                                        <Edit size={10} className="mr-1" />
                                        Manually edited
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            ) : (
                              // Show original input field
                              renderField(question, response)
                            )}
                          </div>

                          {/* Notes field for scale questions */}
                          {question.type && question.type.toLowerCase().includes("scale") && (
                            <div className="mt-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <label className="block text-sm text-gray-300 font-medium">
                                  Notes (appears below slider)
                                </label>
                                <button
                                  type="button"
                                  onClick={() => toggleNotesVisibility(question.id)}
                                  className={`text-xs px-2 py-1 rounded flex items-center ${
                                    response?.notesVisible !== false
                                      ? "bg-green-600/20 text-green-400"
                                      : "bg-red-600/20 text-red-400"
                                  }`}
                                  title={
                                    response?.notesVisible !== false
                                      ? "Notes visible in profile"
                                      : "Notes hidden in profile"
                                  }
                                >
                                  {response?.notesVisible !== false ? (
                                    <>
                                      <Eye size={12} className="mr-1" /> Visible
                                    </>
                                  ) : (
                                    <>
                                      <EyeOff size={12} className="mr-1" /> Hidden
                                    </>
                                  )}
                                </button>
                              </div>
                              <textarea
                                value={response?.notes || ""}
                                onChange={(e) => updateNotes(question.id, e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-white text-sm"
                                rows={3}
                                placeholder="Add notes or explanations for this slider value..."
                                disabled={saving}
                              />
                            </div>
                          )}

                          {/* Optimize button - don't show for brand info section */}
                          {!isBrandInfoSection &&
                            (!isOptimizing ? (
                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleOptimizeResponse(
                                      question.id,
                                      question.title,
                                      response?.value || "",
                                      section.title
                                    )
                                  }
                                  className="text-xs px-2 py-1 bg-purple-600/30 hover:bg-purple-600/50 rounded text-purple-300 flex items-center"
                                  disabled={saving || isOptimizing}
                                  title="Optimize this content for visual display"
                                >
                                  <Sparkles size={12} className="mr-1" />
                                  {isOptimized
                                    ? "Re-optimize"
                                    : "Optimize for Display"}
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-end">
                                <span className="text-xs px-2 py-1 bg-gray-700 rounded text-gray-300 flex items-center">
                                  <RotateCw
                                    size={12}
                                    className="mr-1 animate-spin"
                                  />
                                  Optimizing...
                                </span>
                              </div>
                            ))}

                          {question.description && (
                            <p className="text-gray-400 text-xs">
                              {question.description}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Section Summary Editor - exclude for Brand Info */}
              {narrativeSection && narrativeSection !== "brandInfo" && (
                <div className="border border-gray-700 rounded-lg overflow-hidden mb-8 bg-gray-900/50">
                  <div className="flex justify-between items-center bg-gray-800/80 p-4">
                    <div className="flex items-center">
                      <MessageCircle size={18} className="mr-2 text-green-400" />
                      <h3 className="text-white font-medium">
                        Section Summary: {section.title}
                      </h3>
                    </div>
                  </div>
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Summary Text (displayed in profile)
                      </label>
                      <textarea
                        value={sectionSummaries[narrativeSection] || ""}
                        onChange={(e) =>
                          setSectionSummaries((prev) => ({
                            ...prev,
                            [narrativeSection]: e.target.value,
                          }))
                        }
                        className="w-full min-h-[200px] bg-gray-800 border border-gray-700 rounded-md p-3 text-white font-normal"
                        placeholder="Enter a summary for this section. This will be displayed in the profile instead of individual questions."
                        disabled={saving}
                      />
                      <p className="text-gray-400 text-xs mt-2">
                        This summary will be displayed in the profile visualization instead of individual question responses.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Soundboard Editor Section */}
        <div className="border border-gray-700 rounded-lg overflow-hidden mb-8 bg-gray-900/50">
          <div
            className="flex justify-between items-center bg-gray-800/80 p-4 cursor-pointer"
            onClick={() =>
              setExpandedSections((prev) => ({
                ...prev,
                soundboard: !prev.soundboard,
              }))
            }
          >
            <div className="flex items-center">
              <span className="mr-2 text-yellow-400">🎵</span>
              <h3 className="text-white font-medium">Soundboard</h3>
            </div>
            <ChevronDown
              className={`transform transition-transform text-white/70 ${
                expandedSections.soundboard ? "rotate-180" : ""
              }`}
              size={20}
            />
          </div>
          {expandedSections.soundboard && (
            <div className="p-4 space-y-6">
              {/* Description */}
              <div>
                <label className="block text-white font-medium mb-1">
                  Description
                </label>
                <textarea
                  className="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-white"
                  rows={3}
                  value={data?.soundboard?.description || ""}
                  onChange={(e) =>
                    setData((prev) => ({
                      ...prev,
                      soundboard: {
                        ...prev.soundboard,
                        description: e.target.value,
                      },
                    }))
                  }
                />
              </div>

              {/* Timeline */}
              <div>
                <label className="block text-white font-medium mb-1">
                  Timeline
                </label>
                <ul className="space-y-2">
                  {(data?.soundboard?.timeline || []).map((step, idx, arr) => (
                    <li
                      key={idx}
                      className="flex flex-col gap-2 items-stretch border-b border-gray-700 pb-2 mb-2 bg-gray-800/40 rounded-md p-3"
                    >
                      <div className="flex gap-2 items-center mb-1">
                        <input
                          className="bg-gray-800 border border-gray-700 rounded-md p-2 text-white flex-1"
                          placeholder="Step Title"
                          value={step.title || ""}
                          onChange={(e) => {
                            const newTimeline = [
                              ...(data.soundboard?.timeline || []),
                            ];
                            newTimeline[idx] = {
                              ...newTimeline[idx],
                              title: e.target.value,
                            };
                            setData((prev) => ({
                              ...prev,
                              soundboard: {
                                ...prev.soundboard,
                                timeline: newTimeline,
                              },
                            }));
                          }}
                        />
                        <button
                          className="px-2 py-1 bg-red-700 text-white rounded"
                          onClick={() => {
                            const newTimeline = [
                              ...(data.soundboard?.timeline || []),
                            ];
                            newTimeline.splice(idx, 1);
                            setData((prev) => ({
                              ...prev,
                              soundboard: {
                                ...prev.soundboard,
                                timeline: newTimeline,
                              },
                            }));
                          }}
                        >
                          Remove
                        </button>
                        <button
                          className="px-2 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
                          disabled={idx === 0}
                          onClick={() => {
                            if (idx === 0) return;
                            const newTimeline = [
                              ...(data.soundboard?.timeline || []),
                            ];
                            [newTimeline[idx - 1], newTimeline[idx]] = [
                              newTimeline[idx],
                              newTimeline[idx - 1],
                            ];
                            setData((prev) => ({
                              ...prev,
                              soundboard: {
                                ...prev.soundboard,
                                timeline: newTimeline,
                              },
                            }));
                          }}
                        >
                          ↑
                        </button>
                        <button
                          className="px-2 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
                          disabled={idx === arr.length - 1}
                          onClick={() => {
                            if (idx === arr.length - 1) return;
                            const newTimeline = [
                              ...(data.soundboard?.timeline || []),
                            ];
                            [newTimeline[idx], newTimeline[idx + 1]] = [
                              newTimeline[idx + 1],
                              newTimeline[idx],
                            ];
                            setData((prev) => ({
                              ...prev,
                              soundboard: {
                                ...prev.soundboard,
                                timeline: newTimeline,
                              },
                            }));
                          }}
                        >
                          ↓
                        </button>
                      </div>
                      <textarea
                        className="bg-gray-800 border border-gray-700 rounded-md p-2 text-white min-h-[80px] resize-y"
                        placeholder="Step Content"
                        value={step.textContent || ""}
                        rows={3}
                        onChange={(e) => {
                          const newTimeline = [
                            ...(data.soundboard?.timeline || []),
                          ];
                          newTimeline[idx] = {
                            ...newTimeline[idx],
                            textContent: e.target.value,
                          };
                          setData((prev) => ({
                            ...prev,
                            soundboard: {
                              ...prev.soundboard,
                              timeline: newTimeline,
                            },
                          }));
                        }}
                      />
                    </li>
                  ))}
                </ul>
                <button
                  className="mt-2 px-3 py-1 bg-green-700 text-white rounded"
                  onClick={() =>
                    setData((prev) => ({
                      ...prev,
                      soundboard: {
                        ...prev.soundboard,
                        timeline: [
                          ...(prev.soundboard?.timeline || []),
                          { title: "", textContent: "" },
                        ],
                      },
                    }))
                  }
                >
                  Add Timeline Step
                </button>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-white font-medium mb-1">
                  Notes
                </label>
                <ul className="space-y-2">
                  {(data?.soundboard?.notes || []).map((note, idx) => (
                    <li
                      key={idx}
                      className="flex flex-col gap-2 items-stretch border-b border-gray-700 pb-2 mb-2"
                    >
                      <input
                        className="bg-gray-800 border border-gray-700 rounded-md p-2 text-white mb-1"
                        placeholder="Title"
                        value={note.title || ""}
                        onChange={(e) => {
                          const newNotes = [...(data.soundboard?.notes || [])];
                          newNotes[idx] = {
                            ...newNotes[idx],
                            title: e.target.value,
                          };
                          setData((prev) => ({
                            ...prev,
                            soundboard: { ...prev.soundboard, notes: newNotes },
                          }));
                        }}
                      />
                      <textarea
                        className="bg-gray-800 border border-gray-700 rounded-md p-2 text-white min-h-[100px] resize-y"
                        placeholder="Content"
                        value={note.content || ""}
                        rows={4}
                        onChange={(e) => {
                          const newNotes = [...(data.soundboard?.notes || [])];
                          newNotes[idx] = {
                            ...newNotes[idx],
                            content: e.target.value,
                          };
                          setData((prev) => ({
                            ...prev,
                            soundboard: { ...prev.soundboard, notes: newNotes },
                          }));
                        }}
                      />
                      <button
                        className="px-2 py-1 bg-red-700 text-white rounded w-fit"
                        onClick={() => {
                          const newNotes = [...(data.soundboard?.notes || [])];
                          newNotes.splice(idx, 1);
                          setData((prev) => ({
                            ...prev,
                            soundboard: { ...prev.soundboard, notes: newNotes },
                          }));
                        }}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  className="mt-2 px-3 py-1 bg-green-700 text-white rounded"
                  onClick={() =>
                    setData((prev) => ({
                      ...prev,
                      soundboard: {
                        ...prev.soundboard,
                        notes: [
                          ...(prev.soundboard?.notes || []),
                          { title: "", content: "", label: "" },
                        ],
                      },
                    }))
                  }
                >
                  Add Note
                </button>
              </div>

              {/* Audio/Video Files */}
              <div>
                <label className="block text-white font-medium mb-1">
                  Audio/Video Files
                </label>
                <ul className="space-y-2">
                  {(data?.soundboard?.audioFiles || []).map((audio, idx) => (
                    <li
                      key={idx}
                      className="flex flex-col gap-2 items-stretch border-b border-gray-700 pb-2 mb-2"
                    >
                      <input
                        className="bg-gray-800 border border-gray-700 rounded-md p-2 text-white mb-1"
                        placeholder="Title"
                        value={audio.title || ""}
                        onChange={(e) => {
                          const newAudio = [
                            ...(data.soundboard?.audioFiles || []),
                          ];
                          newAudio[idx] = {
                            ...newAudio[idx],
                            title: e.target.value,
                          };
                          setData((prev) => ({
                            ...prev,
                            soundboard: {
                              ...prev.soundboard,
                              audioFiles: newAudio,
                            },
                          }));
                        }}
                      />
                      {/* Media Type Selection */}
                      <div className="space-y-2 mb-2">
                        <label className="block text-sm font-medium text-white/70">
                          Media Type
                        </label>
                        <div className="flex gap-3">
                          <button
                            onClick={() => {
                              const newAudio = [
                                ...(data.soundboard?.audioFiles || []),
                              ];
                              newAudio[idx] = {
                                ...newAudio[idx],
                                mediaType: "audio",
                              };
                              setData((prev) => ({
                                ...prev,
                                soundboard: {
                                  ...prev.soundboard,
                                  audioFiles: newAudio,
                                },
                              }));
                            }}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                              (!audio.mediaType || audio.mediaType === "audio")
                                ? "bg-green-600 text-white"
                                : "bg-gray-700 text-white/70 hover:bg-gray-600 hover:text-white"
                            }`}
                          >
                            Audio
                          </button>
                          <button
                            onClick={() => {
                              const newAudio = [
                                ...(data.soundboard?.audioFiles || []),
                              ];
                              newAudio[idx] = {
                                ...newAudio[idx],
                                mediaType: "video",
                              };
                              setData((prev) => ({
                                ...prev,
                                soundboard: {
                                  ...prev.soundboard,
                                  audioFiles: newAudio,
                                },
                              }));
                            }}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                              audio.mediaType === "video"
                                ? "bg-green-600 text-white"
                                : "bg-gray-700 text-white/70 hover:bg-gray-600 hover:text-white"
                            }`}
                          >
                            Video
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-2 items-center">
                        <input
                          className="bg-gray-800 border border-gray-700 rounded-md p-2 text-white flex-1"
                          placeholder={`${(!audio.mediaType || audio.mediaType === "audio") ? "Audio" : "Video"} URL`}
                          value={audio.url || ""}
                          onChange={(e) => {
                            const newAudio = [
                              ...(data.soundboard?.audioFiles || []),
                            ];
                            newAudio[idx] = {
                              ...newAudio[idx],
                              url: e.target.value,
                            };
                            setData((prev) => ({
                              ...prev,
                              soundboard: {
                                ...prev.soundboard,
                                audioFiles: newAudio,
                              },
                            }));
                          }}
                        />
                        <button
                          onClick={() =>
                            handleAudioUpload(idx)
                          }
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Upload
                        </button>
                        <button
                          className="px-2 py-1 bg-red-700 text-white rounded w-fit"
                          onClick={() => {
                            const newAudio = [
                              ...(data.soundboard?.audioFiles || []),
                            ];
                            newAudio.splice(idx, 1);
                            setData((prev) => ({
                              ...prev,
                              soundboard: {
                                ...prev.soundboard,
                                audioFiles: newAudio,
                              },
                            }));
                          }}
                        >
                          Remove
                        </button>
                      </div>

                      {/* Multiple Descriptions */}
                      <div className="space-y-2">
                        <label className="block text-gray-400 text-sm">
                          Descriptions:
                        </label>
                        {(Array.isArray(audio.descriptions)
                          ? audio.descriptions
                          : audio.description
                          ? [audio.description]
                          : []
                        ).map((desc, descIdx) => (
                          <div
                            key={descIdx}
                            className="flex gap-2 items-center"
                          >
                            <textarea
                              className="bg-gray-800 border border-gray-700 rounded-md p-2 text-white flex-1 min-h-[60px] resize-y"
                              placeholder="Description"
                              value={desc || ""}
                              rows={2}
                              onChange={(e) =>
                                handleUpdateAudioDescription(
                                  idx,
                                  descIdx,
                                  e.target.value
                                )
                              }
                            />
                            <button
                              className="px-2 py-1 bg-red-700 text-white rounded w-fit h-fit"
                              onClick={() =>
                                handleRemoveAudioDescription(idx, descIdx)
                              }
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <button
                          className="px-2 py-1 bg-blue-700 text-white rounded text-sm"
                          onClick={() => handleAddDescriptionToAudio(idx)}
                        >
                          + Add Description
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <button
                  className="mt-2 px-3 py-1 bg-green-700 text-white rounded"
                  onClick={() =>
                    setData((prev) => ({
                      ...prev,
                  soundboard: {
                    ...prev.soundboard,
                    audioFiles: [
                      ...(prev.soundboard?.audioFiles || []),
                      { title: "", description: "", url: "", mediaType: "audio" },
                    ],
                  },
                }))
              }
            >
              Add Audio/Video File
            </button>
              </div>
            </div>
          )}
        </div>

        {/* Delivery Editor Section */}
        <div className="border border-gray-700 rounded-lg overflow-hidden mb-8 bg-gray-900/50">
          <div
            className="flex justify-between items-center bg-gray-800/80 p-4 cursor-pointer"
            onClick={() =>
              setExpandedSections((prev) => ({
                ...prev,
                delivery: !prev.delivery,
              }))
            }
          >
            <div className="flex items-center">
              <span className="mr-2 text-green-400">🚚</span>
              <h3 className="text-white font-medium">Delivery</h3>
            </div>
            <ChevronDown
              className={`transform transition-transform text-white/70 ${
                expandedSections.delivery ? "rotate-180" : ""
              }`}
              size={20}
            />
          </div>
          {expandedSections.delivery && (
            <div className="p-4 space-y-8">
              {/* Google Drive URL Input */}
              <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
                <h4 className="text-white font-medium mb-2 flex items-center">
                  <ExternalLink size={18} className="mr-2" />
                  Google Drive Folder URL
                </h4>
                <input
                  type="url"
                  value={deliveryDriveUrl}
                  onChange={(e) => setDeliveryDriveUrl(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-white"
                  disabled={saving}
                />
                <p className="text-gray-400 text-xs mt-2">
                  Enter the Google Drive folder URL for delivery assets. This will replace the ZIP download button on the delivery page.
                </p>
              </div>

              {/* Delivery Sections List */}
              <div className="space-y-4">
                {(data?.deliverySections || []).map((section, idx) => (
                  <div
                    key={idx}
                    className="border border-gray-700 rounded-lg p-4 mb-6 bg-gray-800/60"
                  >
                    <div className="flex gap-2 mb-4">
                      {/* Move Up/Down Buttons */}
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveDeliverySectionUp(idx)}
                          disabled={idx === 0}
                          className={`p-1 rounded text-white transition-colors ${
                            idx === 0
                              ? "text-gray-500 cursor-not-allowed"
                              : "text-gray-300 hover:text-white hover:bg-gray-600"
                          }`}
                          title="Move up"
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          onClick={() => moveDeliverySectionDown(idx)}
                          disabled={
                            idx === (data.deliverySections || []).length - 1
                          }
                          className={`p-1 rounded text-white transition-colors ${
                            idx === (data.deliverySections || []).length - 1
                              ? "text-gray-500 cursor-not-allowed"
                              : "text-gray-300 hover:text-white hover:bg-gray-600"
                          }`}
                          title="Move down"
                        >
                          <ChevronDownIcon size={16} />
                        </button>
                      </div>

                      <input
                        className="flex-1 bg-gray-800 border border-gray-700 rounded-md p-2 text-white"
                        placeholder="Section Title"
                        value={section.title || ""}
                        onChange={(e) => {
                          const newSections = [
                            ...(data.deliverySections || []),
                          ];
                          newSections[idx] = {
                            ...newSections[idx],
                            title: e.target.value,
                          };
                          setData((prev) => ({
                            ...prev,
                            deliverySections: newSections,
                          }));
                        }}
                      />
                      <button
                        className="px-2 py-1 bg-red-700 text-white rounded"
                        onClick={() => {
                          const newSections = [
                            ...(data.deliverySections || []),
                          ];
                          newSections.splice(idx, 1);
                          setData((prev) => ({
                            ...prev,
                            deliverySections: newSections,
                          }));
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    {/* Media Type Selection */}
                    <div className="space-y-2 mb-4">
                      <label className="block text-sm font-medium text-white/70">
                        Media Type
                      </label>
                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            const newSections = [
                              ...(data.deliverySections || []),
                            ];
                            newSections[idx] = {
                              ...newSections[idx],
                              mediaType: "video",
                            };
                            setData((prev) => ({
                              ...prev,
                              deliverySections: newSections,
                            }));
                          }}
                          className={`px-4 py-2 rounded-lg transition-colors ${
                            section.mediaType === "video"
                              ? "bg-green-600 text-white"
                              : "bg-gray-700 text-white/70 hover:bg-gray-600 hover:text-white"
                          }`}
                        >
                          Video
                        </button>
                        <button
                          onClick={() => {
                            const newSections = [
                              ...(data.deliverySections || []),
                            ];
                            newSections[idx] = {
                              ...newSections[idx],
                              mediaType: "audio",
                            };
                            setData((prev) => ({
                              ...prev,
                              deliverySections: newSections,
                            }));
                          }}
                          className={`px-4 py-2 rounded-lg transition-colors ${
                            section.mediaType === "audio"
                              ? "bg-green-600 text-white"
                              : "bg-gray-700 text-white/70 hover:bg-gray-600 hover:text-white"
                          }`}
                        >
                          Audio
                        </button>
                      </div>
                    </div>

                    {/* Category Selection */}
                    <div className="space-y-2 mb-4">
                      <label className="block text-sm font-medium text-white/70">
                        Category Label
                      </label>
                      <p className="text-xs text-white/50">
                        Enter a custom category label to help users filter this
                        content (e.g., "Brand Story", "Behind the Scenes", "Case
                        Study")
                      </p>
                      <div className="flex gap-2">
                        <input
                          className="flex-1 bg-gray-800 border border-gray-700 rounded-md p-2 text-white text-sm"
                          placeholder={
                            section.mediaType === "audio"
                              ? "e.g., Music, Voice Over, Podcast"
                              : "e.g., Brand Story, Commercial, Tutorial"
                          }
                          value={section.categoryLabel || ""}
                          onChange={(e) => {
                            const newSections = [
                              ...(data.deliverySections || []),
                            ];
                            newSections[idx] = {
                              ...newSections[idx],
                              categoryLabel: e.target.value,
                            };
                            setData((prev) => ({
                              ...prev,
                              deliverySections: newSections,
                            }));
                          }}
                        />
                        <button
                          onClick={() => {
                            const newSections = [
                              ...(data.deliverySections || []),
                            ];
                            newSections[idx] = {
                              ...newSections[idx],
                              categoryLabel:
                                section.mediaType === "audio"
                                  ? "Audio"
                                  : "Video",
                            };
                            setData((prev) => ({
                              ...prev,
                              deliverySections: newSections,
                            }));
                          }}
                          className="px-3 py-2 bg-gray-700 text-white/70 rounded-md hover:bg-gray-600 hover:text-white transition-colors text-xs"
                          title="Use media type as category"
                        >
                          Auto
                        </button>
                      </div>
                    </div>

                    {/* Media URL Input with upload button */}
                    <div className="flex items-center gap-4 mb-4">
                      <input
                        className="flex-1 bg-gray-800 border border-gray-700 rounded-md p-2 text-white"
                        placeholder={`${
                          section.mediaType === "audio" ? "Audio" : "Video"
                        } URL`}
                        value={section.mediaUrl || ""}
                        onChange={(e) => {
                          const newSections = [
                            ...(data.deliverySections || []),
                          ];
                          newSections[idx] = {
                            ...newSections[idx],
                            mediaUrl: e.target.value,
                          };
                          setData((prev) => ({
                            ...prev,
                            deliverySections: newSections,
                          }));
                        }}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            handleDeliveryMediaUpload(
                              idx,
                              section.mediaType || "video"
                            )
                          }
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Upload
                        </button>
                        <button
                          onClick={() =>
                            testMediaUrl(
                              section.mediaUrl,
                              section.mediaType || "video"
                            )
                          }
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          Test
                        </button>
                      </div>
                    </div>

                    {/* Paragraphs */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h6 className="text-sm font-medium text-white">
                          Paragraphs
                        </h6>
                        <button
                          onClick={() => addParagraphToDeliverySection(idx)}
                          className="px-3 py-1 bg-gray-700 text-white/70 rounded-lg hover:bg-gray-600 hover:text-white transition-colors text-sm"
                        >
                          Add Paragraph
                        </button>
                      </div>
                      {(section.paragraphs || [""]).map(
                        (paragraph, paraIndex) => (
                          <div key={paraIndex} className="relative">
                            <textarea
                              className="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-white min-h-[80px] resize-y"
                              placeholder={`Paragraph ${paraIndex + 1}`}
                              value={paragraph}
                              onChange={(e) =>
                                updateParagraphInDeliverySection(
                                  idx,
                                  paraIndex,
                                  e.target.value
                                )
                              }
                              rows={3}
                            />
                            {section.paragraphs?.length > 1 && (
                              <button
                                onClick={() =>
                                  removeParagraphFromDeliverySection(
                                    idx,
                                    paraIndex
                                  )
                                }
                                className="absolute top-2 right-2 p-1 text-red-400 hover:text-red-300"
                              >
                                <X size={16} />
                              </button>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button
                className="mt-2 px-3 py-1 bg-green-700 text-white rounded"
                onClick={() =>
                  setData((prev) => ({
                    ...prev,
                    deliverySections: [
                      ...(prev.deliverySections || []),
                      {
                        title: "",
                        mediaType: "video",
                        mediaUrl: "",
                        paragraphs: [""],
                      },
                    ],
                  }))
                }
              >
                Add Delivery Section
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer with save button */}
      <div className="mt-8 flex justify-between">
        {Object.keys(optimizedResponses).length > 0 && (
          <button
            onClick={saveOptimizedResponses}
            className="flex items-center px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded text-white"
            disabled={saving}
          >
            <Sparkles className="mr-2" size={18} />
            {saving ? "Saving..." : "Save Optimized Content"}
          </button>
        )}
        <button
          onClick={handleSave}
          className="flex items-center px-6 py-2 bg-green-600 hover:bg-green-700 rounded text-white"
          disabled={saving}
        >
          <Save className="mr-2" size={18} />
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default DynamicProfileEditor;
